import { notification } from "antd"
import { Loader } from "lucide-react"
import {
  lazy,
  memo,
  MutableRefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router"
import { useShallow } from "zustand/react/shallow"

import { useQueryClient } from "@tanstack/react-query"
import AgentChatInput, {
  AgentChatInputHandle
} from "~/components/agent-chat-input"
import { createOsAgentsSession, getChatSuggestions } from "~/api/os.agents"
import URLS from "~/constants/urls"
import { EVENTS, getCardPreviewKey } from "~/containers/os-agents/constants"
import { useGetAccounts } from "~/hooks/accounts"
import useGlobalStore from "~/store/global"
import useOsAgentsIdStore from "~/store/os.agents_.$id"

import { IAccount } from "~/types/accounts"
import { ITokenInfo } from "~/types/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { withLinkedTenant } from "~/utils/linked-tenant"

import AgentSelectAccount from "./agent-select-account"
import {
  friendlyDecisionActionLabel,
  humanizeClarificationKey
} from "./v2-history-replay"

const OtherTools = lazy(() => import("./other-tools"))

interface Tool {
  id: string
  name: string
  description: string
}

// The agent-search PMB tool card (shown as "Prep for your meeting") always
// generates the DEFAULT brief — clicking it skips the Q1/Q3 clarification
// picker (which auto-answers with mode="default").
//
// Tool cards come from the backend /copilot/agent_tools response; a card's
// `id` is the agent class name (the same string used for the icon path
// /images/os-agent/<id>.png and for transfer_to_agent routing). The human
// `name`/`description` are display-only and can be reworded backend-side
// ("Prep for your meeting" ≠ "Pre-meeting brief"), so we match on the
// stable agent id only. "GeneratePersonPreMeetingBriefAgent" is the wire
// name py aliases the v2 agent to; the V2 string is kept as a safety net.
const PMB_TOOL_IDS = [
  "GeneratePersonPreMeetingBriefAgent",
  "GeneratePreMeetingBriefAgentV2"
]

function isPreMeetingBriefTool(item: Tool): boolean {
  return PMB_TOOL_IDS.includes(item?.id)
}

const ToolCard = memo(
  ({
    item,
    isLoading,
    onClick
  }: {
    item: Tool
    isLoading: boolean
    onClick: () => void
  }) => (
    <button onClick={onClick} className="flex flex-col group text-left">
      <div
        className={`relative h-36 border-[0.5px] rounded-t-lg bg-linear-to-r from-[#FAFAFA] to-[rgba(219, 218, 224, 0.95)] dark:from-[#3C3C3E] dark:to-[#1e1e1e] shadow-inner ${isLoading ? "border-primary" : "group-hover:border-primary dark:group-hover:border-gray-400 border-light-border dark:border-gray-600"}`}
      >
        <img
          src={`/images/os-agent/${item.id}.png`}
          alt={item.name}
          className="object-contain h-full w-full scale-75"
          onError={(e) => {
            e.currentTarget.src =
              "/images/os-agent/GenerateAccountPrioritiesAgent.png"
          }}
        />
        {isLoading && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <Loader
              size={32}
              strokeWidth={2}
              className="text-primary animate-spin"
            />
          </div>
        )}
      </div>
      <div
        className={`flex flex-col gap-1 p-2 border-[0.5px] border-t-0 rounded-b-lg flex-1 ${isLoading ? "border-primary" : "group-hover:border-primary dark:group-hover:border-gray-400 border-light-border dark:border-gray-600"}`}
      >
        <h6 className="text-xs font-medium">{item.name}</h6>
        <p className="text-xs text-gray-500">{item.description}</p>
      </div>
    </button>
  )
)
ToolCard.displayName = "ToolCard"

let abortController: AbortController | null = null
/**
 * True when the current abort was triggered by the resume handler
 * (the user clicked an ask_user option, we intentionally close the
 * still-open first stream before opening the resume stream). The
 * sendMessage AbortError catch branch reads this flag and does NOT
 * paint the prior message as errored when it's set — without this we
 * leaked "signal is aborted without reason" into the message bubble
 * every time the user answered a clarification (2026-04-24 bug).
 */
let intentionalAbort = false

/**
 * Module-level guard: true while a /resume POST is mid-stream. Lets us
 * silently drop a duplicate Submit/Approve click (double-click, repeated
 * spacebar, etc.) before it reaches the network. The BE has its own
 * race guard returning 409, but catching the duplicate here avoids the
 * round-trip and the brief "Trying to send..." flicker.
 */
let resumeInFlight = false

/**
 * Sessions whose AgentSearch instance has deactivated (user navigated to
 * a different chat or off the route entirely) and have NOT yet been re-
 * activated. The dying stream's tail-check / AbortError catch reads
 * this set: if its `sessionId` is here, skip the canonical-error paint
 * — the BE worker keeps running and the route-mount reattach hint will
 * pick it up on return (ENG-1325).
 *
 * Race-safe across React's cleanup→effect-body sequence because the
 * marker is set in cleanup (captures the OLD session id) and removed
 * in the effect body (captures the NEW session id). The dying async
 * fn's check uses its own closure-captured sessionId, so it always
 * sees the marker for ITS session regardless of timing.
 */
const deactivatedSessions = new Set<string>()

/**
 * Parse the `id:` event_id from an SSE frame (returns null if missing
 * / non-numeric). Used to track the highest event_id processed on each
 * bubble so a subsequent reattach — which BE replays from event_id=0
 * — can skip frames already seen and avoid duplicating agentSteps in
 * the UI (ENG-1325 follow-up).
 */
function _parseSseEventId(frame: string): number | null {
  const idLine = frame.split("\n").find((l) => l.startsWith("id:"))
  if (!idLine) return null
  const parsed = parseInt(idLine.slice(3).trim(), 10)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Two surfaces share this component:
 *   - mode="ask" (default): the Ask-OS drawer; account + opportunity
 *     scope, no RFP context.
 *   - mode="rfp": the RFP-OS sub-session chat; pins the planner into
 *     RFP-OS mode (py-side flag is_rfp_os=true), surfaces the Deep
 *     Research toggle in the chat input, and forwards
 *     rfp_additional_context (rfp_id, rfp_question_id, rfp_summary,
 *     rfp_scope) on every send.
 *
 * Generic features (chat history, attached files, drag-drop, quoted
 * text, deep-research toggle) live HERE — there is no V2 fork. The
 * RFP-OS panel mounts this component when preferences.enable_agent_v2
 * is on (mirrors the Ask-OS swap in os.agents.tsx#L41).
 */
/** The generated page the side panel has open, or "" — read at call time
 *  because sendMessage is memoised and would close over a stale value. */
function htmlPagePanelIsOpen(): boolean {
  return (
    useOsAgentsIdStore.getState().showSplitter?.resourceName ===
    "HtmlPageBuilder"
  )
}

/** The open page's id. A freshly generated card carries none, so the panel
 *  publishes it to the store once the session exists. */
function openHtmlPageSessionId(): string {
  const store = useOsAgentsIdStore.getState()
  if (!htmlPagePanelIsOpen()) return ""
  return (
    store.openHtmlPageSessionId ||
    store.showSplitter?.resourceParams?.session_id ||
    ""
  )
}

const AgentSearch = ({
  className = "",
  agentToolsList,
  otherAgentToolsList,
  kosToolsList,
  otherKosToolsList,
  onSubmitRef,
  mode = "ask",
  rfpAdditionalContext,
  parentSessionId,
  sessionIdOverride,
  opportunityIdOverride,
  accountIdOverride,
  createSession,
  embedded = false,
  onSessionCreated,
  autoOpenTemplatePicker = false,
  defaultTransferToAgent,
  isAccountPlan
}: {
  className?: string
  agentToolsList?: Tool[]
  otherAgentToolsList?: Tool[]
  kosToolsList?: Tool[]
  otherKosToolsList?: Tool[]
  onSubmitRef?: MutableRefObject<
    (data: {
      tool_id?: string
      agentQuery?: string
      accountId?: string
      files?: {
        name: string
        id: string
        source?: string
        original_s3_path?: string
        file_extension?: string
      }[]
      transferToAgent?: string
      displayText?: string
    }) => void
  >
  /** Switches the body shape posted to api-getorbitx. Default "ask". */
  mode?: "ask" | "rfp"
  /** RFP-OS context — required when mode==="rfp". */
  rfpAdditionalContext?: {
    rfp_id: string
    rfp_question_id?: string
    rfp_summary?: string
    rfp_scope?: string
  }
  /** RFP parent session id (the active RFP). Forwarded as
   *  rfp_additional_context.rfp_id when the caller didn't supply one. */
  parentSessionId?: string
  /** Override the route-based existingSessionId lookup. RFP-OS uses
   *  its own subSessionId from useRfpOsQnaStore; passing it here
   *  keeps `useParams().id` (which is the parent rfp_id) from being
   *  mistaken for the chat session. Empty string ⇒ "fresh session". */
  sessionIdOverride?: string
  /** Override the opportunity_id sent in the request body. Used by
   *  the Sales Pitch Workbench overlay which mounts AgentSearchV2
   *  outside the normal /os/agents route, so `?opportunity=` search
   *  params are absent. Takes priority over the URL param. */
  opportunityIdOverride?: string
  /** Override the URL-based `?account=` lookup. The workbench overlays
   *  (sales-pitch + PMB v2) mount AgentSearchV2 outside /os/agents and
   *  don't want to mutate the underlying page's URL to wire the account.
   *  When set, this id seeds `selectedAccount` (which anchors the planner
   *  prompt + body.account_id) AND surfaces to AgentSelectAccount as the
   *  displayed value. User-driven picker changes still write to URL — the
   *  override only seeds the initial state. */
  accountIdOverride?: string
  /** Account-plan workbench (ENG-2274). When true, every turn posts
   *  `is_account_plan: true` so the backend approval_gate direct-routes
   *  to GenerateAccountPlanV2Agent — skipping the planner + the plan
   *  approval gate (mirrors the sales-pitch fast lane). Without it the
   *  turn falls through to the normal planner (5-step plan + approval). */
  isAccountPlan?: boolean
  /** Override the Ask-OS session-create call (createOsAgentsSession).
   *  RFP-OS supplies useCreateAgentSession instead, returning the new
   *  sub-session id. Called on the FIRST send in a fresh session,
   *  with the user's actual message + the chat-input's
   *  researchEnabled state — this matches V1 RFP fresh-session.tsx,
   *  where the question becomes the session's title. */
  createSession?: (opts: {
    firstQuery: string
    researchEnabled: boolean
  }) => Promise<string>
  /** Embedded (drawer/panel) mode — e.g. the agentOS "Fill Templates" drawer.
   *  Suppresses the post-create `/os/agents/:id` redirect so a fresh session
   *  stays in the drawer (no URL change). The new session id is surfaced via
   *  `onSessionCreated` instead, so the host can point its embedded message
   *  view at it. */
  embedded?: boolean
  /** Fires once with the new session id when a fresh session is created in
   *  embedded mode (the host then feeds it to AgentIdMessagesV2). */
  onSessionCreated?: (sessionId: string) => void
  /** Forwarded to AgentChatInput: auto-open the My Files picker in template
   *  mode on mount (agentOS "Fill Templates" entry). */
  autoOpenTemplatePicker?: boolean
  /** Pin every send from this surface to one agent (fast-lane hint).
   *  Used by the dedicated workbench overlays (sales-pitch, PMB v2): the
   *  initial auto-fired query already carries transferToAgent, but a
   *  user-TYPED follow-up ("change the background color of slide 2")
   *  previously reached the planner hint-less — and the planner cannot
   *  route to hint-only agents like GenerateSalesPitchDeckV2Agent (it is
   *  deliberately excluded from the catalog because its generate flow
   *  needs this exact workbench surface to open the SSE stream). An
   *  explicit per-send transferToAgent still wins over this default. */
  defaultTransferToAgent?: string
}) => {
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [openMore, setOpenMore] = useState(false)
  const { id: routeId } = useParams()
  // sessionIdOverride wins when set — RFP-OS passes its own sub-
  // session id so we don't mistake the parent rfp_id (params.id on
  // the rfp-rfi/qna route) for the chat session.
  const existingSessionId =
    typeof sessionIdOverride === "string" ? sessionIdOverride : routeId
  const navigate = useNavigate()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  // NOTE (v2 migration): setShowSplitter and pitchDeckQueryClient are
  // retained but currently unused because the v2 pipeline does not yet
  // emit ui_component frames for PresentationOutlineBuilder /
  // SalesPitchDeckBuilder. Those features dispatch through feature
  // sub-agents (presentationBuilderAgent, salesPitchDeckAgent) whose
  // output is captured as plain step text. When py-processing is extended
  // to emit a ui_component-shaped frame from the planner executor, rewire
  // the split-view trigger + pitch-deck react-query invalidation back
  // into processV2Frame (case "ui_component" — or a plan_step_completed
  // subtype carrying component metadata). The referenced imports + refs
  // are intentionally kept so the wiring is a localized add, not a
  // re-import pass.
  const { setMessages, setShowSplitter, setPendingMessage } =
    useOsAgentsIdStore(
      useShallow((store) => ({
        setMessages: store.setMessages,
        setShowSplitter: store.setShowSplitter,
        setPendingMessage: store.setPendingMessage
      }))
    )
  const { pendingMessage, isPptBusy, isPageBusy } = useOsAgentsIdStore(
    useShallow((store) => ({
      pendingMessage: store.pendingMessage,
      // Keyed on existingSessionId, not routeId: the workbench has no route
      // param, so routeId left the deck clause dead there and the composer
      // re-enabled as soon as the agent replied, while the deck was still
      // rendering.
      isPptBusy:
        store.pptOutlineState?.slidesStatus === "generating" ||
        store.pptOutlineState?.isStreaming ||
        (existingSessionId != null &&
          (store.pitchDeckV2ByChat[existingSessionId]?.status ===
            "generating" ||
            !!store.pitchDeckV2ByChat[existingSessionId]?.isIterating)),
      // A generated web page renders in the side panel and takes about as long
      // as a deck, so the composer waits on it too.
      isPageBusy:
        existingSessionId != null &&
        !!store.htmlPageBusyByChat[existingSessionId]
    }))
  )

  // Drag-drop hand-off: the global drag overlay drops File[] into the store;
  // we forward them into AgentChatInput's hook and clear the store.
  const droppedFiles = useOsAgentsIdStore((store) => store.uploadedFiles)
  const clearDroppedFiles = useOsAgentsIdStore(
    (store) => store.clearUploadedFiles
  )
  const sessionMessages = useOsAgentsIdStore(
    (store) => store.messages[existingSessionId ?? ""]
  )
  const historyFileNames = useMemo(() => {
    const names = new Set<string>()
    for (const msg of Object.values(sessionMessages ?? {}) as any[]) {
      for (const f of msg?.files ?? []) {
        if (f?.name) names.add(f.name)
      }
    }
    return Array.from(names)
  }, [sessionMessages])

  // ENG-1274: when the pipeline pauses on an askUser prompt (plan
  // approval, mid-plan clarification, execution_ask_user, etc.) the
  // user must answer via the inline form — typing a free-text message
  // would either submit a fresh turn alongside the unanswered approval
  // (race) or get silently dropped. Treat any non-null askUser as a
  // chat-input block until the resume handler clears it. ALSO block
  // while any bubble is still `isPending` — covers the resume / retry
  // / reattach streams which don't touch `pendingMessage` (only the
  // initial sendMessage does), so without this gate the input is
  // interactable during post-approve execution and after a reattach
  // (ENG-1274 regression).
  const hasPendingTurn = useMemo(() => {
    if (!sessionMessages) return false
    for (const msg of Object.values(sessionMessages) as any[]) {
      if (msg?.askUser) return true
      if (msg?.isPending) return true
    }
    return false
  }, [sessionMessages])

  const pitchDeckQueryClient = useQueryClient()
  void pitchDeckQueryClient // reserved for v2 ui_component wiring (see note above)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const isProactiveProposalRoute = location.pathname.startsWith(
    "/rfp-os/proactive-proposal"
  )
  const messageRef = useRef<HTMLTextAreaElement>(null)
  const chatInputRef = useRef<AgentChatInputHandle>(null)
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)
  const pitchDeckV2SeenRef = useRef(false)
  const pitchDeckV2SplitterSetRef = useRef(false)
  const pitchDeckV2AccountIdRef = useRef("")
  // Holds the `sources` list from the most recent `sources_collected`
  // event of the CURRENT turn. The planner/executor path emits sources
  // here (leaf-step web sources) but doesn't always echo them onto the
  // `done` frame, so the Sources chips would be missing even though the
  // step says "Collected N sources". The done handler falls back to this
  // when its own web_search_results is empty. Reset per turn.
  const leafWebSourcesRef = useRef<any[]>([])
  // The workbench overlays (sales-pitch + PMB v2) launch outside
  // /os/agents and seed the account via prop instead of mutating the
  // underlying page's `?account=`. When set, the prop wins (the overlay
  // knows the locked account); otherwise fall back to the URL param,
  // preserving normal Ask-OS / RFP-OS behavior.
  const selectedAccount = accountIdOverride ?? searchParams.get("account")
  const selectedOpportunity = searchParams.get("opportunity")
  const actionParam = searchParams.get("action")
  const headerAskQueryParam = searchParams.get("query")
  const { t } = useTranslation("common")

  // Resolve the canonical name for the account currently pinned in the
  // URL — needed so we can expand the "selected account" placeholder
  // (used by suggestion chips + free-text typed by the user) into the
  // actual `<name> (internal account id: <id>)` string before the
  // payload is sent. Otherwise the planner has to guess which account
  // "the selected account" refers to, and Haiku has been observed
  // emitting a spurious "Which account?" clarification despite the
  // account being pinned in the UI (RCA inv 009eed90).
  const { data: accountsData } = useGetAccounts(undefined, true)
  const selectedAccountName = useMemo(() => {
    if (!selectedAccount || selectedAccount === "all_accounts") return ""
    const accounts: IAccount[] = accountsData?.data?.accounts ?? []
    const hit = accounts.find((a) => `${a.id}` === `${selectedAccount}`)
    return hit?.account_name || hit?.short_name || ""
  }, [selectedAccount, accountsData])

  // Quoted-text passthrough — store-backed so the message rows
  // (agent-id-messages-v2.tsx) and the chat-input chip share state.
  // Same store/keys Ask-OS V1 uses (see agent-search.tsx#L126), so
  // `setQuotedText` from any AgentQuoteSelection click lands here
  // and the next send carries it as `context_quote`.
  const quotedText = useOsAgentsIdStore((store) => store.quotedText)
  const setQuotedText = useOsAgentsIdStore((store) => store.setQuotedText)
  const quotedTextRef = useRef<string | null>(null)
  quotedTextRef.current = quotedText
  // Resolve the rfp_additional_context the BE expects — caller can
  // supply explicit rfpAdditionalContext OR just parentSessionId
  // (which we treat as rfp_id).
  const resolvedRfpContext = useMemo(() => {
    if (mode !== "rfp") return null
    const ctx = { ...rfpAdditionalContext } as {
      rfp_id?: string
      rfp_question_id?: string
      rfp_summary?: string
      rfp_scope?: string
    }
    if (!ctx.rfp_id && parentSessionId) {
      ctx.rfp_id = parentSessionId
    }
    return ctx
  }, [mode, rfpAdditionalContext, parentSessionId])

  // Canonical, user-facing error bubbles for the v2 chat flow. Localised so
  // they pick up the active language. See note on the original module-level
  // constants for the product rationale (uniform messaging across abort /
  // network / HTTP / stream-drop / pipeline failure modes).
  const GENERIC_ERROR_MESSAGE = t("os_agents.label_generic_error")
  // BE returns 409 when the user resolved this turn elsewhere or another
  // tab is racing — distinct copy so we can guide the user to refresh.
  const STALE_SESSION_MESSAGE = t("os_agents.label_stale_session")

  const toolsList = useMemo(() => {
    return selectedAccount ? agentToolsList : kosToolsList
  }, [selectedAccount, agentToolsList, kosToolsList])
  const otherToolsList = useMemo(
    () => (selectedAccount ? otherAgentToolsList : otherKosToolsList),
    [selectedAccount, otherAgentToolsList, otherKosToolsList]
  )

  const sendMessage = useCallback(
    async ({
      sessionId,
      message,
      selectedAccount,
      tool_id,
      files = [],
      researchEnabled = false,
      isTemplateFill = false,
      transferToAgent,
      pmbBriefId,
      pmbContext,
      pmbForceDefault,
      displayText,
      source,
      projectName,
      onPostCommitted
    }: {
      sessionId: string
      message: string
      selectedAccount?: string
      tool_id?: string
      // Proactive-proposal markers. When source==="proactive_proposal",
      // api-getorbitx stamps ui_component.params.source on the in_progress
      // history stub at POST time, so the session shows in the "Past
      // proactive proposals" list (and is resumable) immediately — not only
      // after the agent's first PptBuilderV2 frame ~60-90s later.
      source?: string
      projectName?: string
      files?: {
        name: string
        id: string
        source?: string
        original_s3_path?: string
        file_extension?: string
      }[]
      researchEnabled?: boolean
      // Template-fill toggle from chat input. When true (and at least one
      // attached file is xlsx/docx), py routes to TemplateFillerAgent
      // instead of the default chat-doc RAG path.
      isTemplateFill?: boolean
      // FE → py routing hint. When set (e.g. PresentationBuilderAgentV2
      // for a Generate Slides click), py's approval gate synthesizes a
      // 1-step plan and skips both root + planner LLM calls. Click-driven
      // UI flows pass this; free-text user typing leaves it undefined.
      transferToAgent?: string
      // PMB v2 hand-off — entry button + per-section regenerate.
      pmbBriefId?: string
      pmbContext?: Record<string, unknown> | null
      // When true, this PMB v2 turn auto-answers the Q1/Q3 clarification
      // picker with mode="default" the moment it arrives — used by the
      // agent-search "Pre-meeting brief" tool card so a card click always
      // produces the default brief without prompting. The picker
      // component (agent-steps-ui-v2) reads `_pmbForceDefault` off the
      // message and fires the default resume on mount.
      pmbForceDefault?: boolean
      // When set, the user-bubble's content shows displayText instead of
      // the full prompt — chip click sends prompt to BE but shows label.
      displayText?: string
      // Fires synchronously once the POST has been ACK'd by the backend
      // (response.ok, body present) OR has definitively failed. Used by
      // callers that need to defer a `navigate(...)` until after the
      // request is in flight — without this, the chunk-error reload
      // triggered by route-load on a fresh deployment can tear down the
      // in-flight POST and orphan the session. Stream reading continues
      // in the background after this fires.
      onPostCommitted?: () => void
    }) => {
      if (abortController) {
        abortController.abort()
      }
      pitchDeckV2SeenRef.current =
        transferToAgent === "GenerateSalesPitchDeckV2Agent"
      pitchDeckV2SplitterSetRef.current = false
      // Clear stale right-panel content from a prior flow (e.g. pitch deck
      // session still showing when user starts a PPT builder query).
      if (transferToAgent !== "GenerateSalesPitchDeckV2Agent") {
        setShowSplitter(null)
      }
      pitchDeckV2AccountIdRef.current =
        accountIdOverride ?? selectedAccount ?? ""
      // Capture quoted text BEFORE the await so the setQuotedText(null)
      // below doesn't race with the body assembly (mirrors the V1 RFP
      // container's pattern at continue-session-chat.tsx).
      const capturedQuotedText = quotedTextRef.current
      setQuotedText(null)
      // Mirror Node's chatbot-v2.js#L1029-L1034 prefix shape into the
      // local user-bubble content so parseQuotedMessage extracts the
      // excerpt for the `↳ quote` UI block identically in LIVE and
      // REPLAY paths. Without this, the live bubble shows just the
      // bare question (no quote chip), then page reload "magically"
      // adds the quote chip — the persisted history row carries the
      // prefix Node prepended, while the live store carried only the
      // bare text.
      const rawText = displayText || message
      const persistedUserContent =
        capturedQuotedText && capturedQuotedText.trim()
          ? `Regarding this excerpt from a previous response:\n"${capturedQuotedText.trim()}"\n\n${rawText}`
          : rawText
      setMessages(sessionId, {
        role: "user",
        content: persistedUserContent,
        id: Date.now().toString(),
        researchEnabled: !!researchEnabled,
        timestamp: Date.now(),
        files
      })
      const assistantMessageId = (Date.now() + 1).toString()
      // Fresh turn — clear any leaf web-sources captured from a prior
      // turn's sources_collected so they can't leak onto this message.
      leafWebSourcesRef.current = []
      // Local reference so the finally block can identity-compare and
      // avoid nulling-out a newer controller installed by a concurrent
      // resumeWithDecision.
      const myAbortController = new AbortController()
      // onPostCommitted gate — fires at most once. Called once the POST
      // has either succeeded (response.ok + body) or failed in a way
      // the caller can react to (response thrown / network error). We
      // run it from BOTH the success path (right before stream-read
      // starts) AND the catch block, so the caller's navigate fires no
      // matter the outcome — otherwise an early POST failure would
      // leave the user stranded on the empty agent page with the error
      // bubble parked in the now-orphan session.
      let postCommittedFired = false
      const firePostCommitted = () => {
        if (postCommittedFired) return
        postCommittedFired = true
        try {
          onPostCommitted?.()
        } catch (err) {
          // caller's navigate must not break the stream — but surface
          // so the failure is visible during debugging (ENG-1167/1293).
          // eslint-disable-next-line no-console
          console.error("[v2-debug] onPostCommitted threw", err)
        }
      }
      try {
        abortController = myAbortController

        const body: any = {
          query: message,
          // Mode-aware py state pinning. is_rfp_os=true makes the
          // py planner register the RFP answer-bank tool and forbid
          // tenant-knowledge substitutes; deep_research routes the
          // tool's RAG-vs-ML branch. is_rfp_os=false makes the
          // plan-validation guard reject any plan that tries to use
          // the RFP agent on an Ask-OS turn.
          is_rfp_os: mode === "rfp",
          deep_research: !!researchEnabled,
          researchEnabled: !!researchEnabled, // accepted alias on the BE
          // Template-fill mode: when true, py's worker seeds
          // state.is_template_fill and the approval gate force-routes to
          // TemplateFillerAgent. No-op when the attached file isn't a
          // fillable xlsx/docx (py treats it as a missing precondition).
          is_template_fill: !!isTemplateFill,
          // Read at send time: sendMessage is memoised on [tokenInfo], so a
          // value closed over here is the one from before any page existed.
          is_html_page_iterate: htmlPagePanelIsOpen(),
          // Name the page, so the edit patches the one on screen rather than
          // whichever page this user finished most recently.
          html_page_session_id: openHtmlPageSessionId()
        }

        if (mode === "rfp" && resolvedRfpContext) {
          body.rfp_additional_context = resolvedRfpContext
          if (parentSessionId) {
            body.parent_session_id = parentSessionId
          }
        }
        if (capturedQuotedText) {
          body.context_quote = capturedQuotedText
        }

        const resolvedAccount = accountIdOverride ?? selectedAccount
        if (resolvedAccount) {
          body.account_id = resolvedAccount
        }
        const resolvedOpportunity = opportunityIdOverride ?? selectedOpportunity
        if (resolvedOpportunity) {
          body.opportunity_id = resolvedOpportunity
        }
        if (tool_id) {
          body.tool_id = tool_id
        }
        // Proactive-proposal markers — api-getorbitx pre-stamps the
        // in_progress history-stub's ui_component.params with these so the
        // session is listed + resumable from the moment the POST returns.
        if (source) {
          body.source = source
        }
        if (projectName) {
          body.project_name = projectName
        }
        if (transferToAgent) {
          body.transfer_to_agent = transferToAgent
        }
        // Account-plan workbench direct-route (ENG-2274 skips planner+gate). A
        // .pptx attachment here means "fill this uploaded template" (ENG-1715).
        if (isAccountPlan) {
          const hasPptxUpload =
            Array.isArray(files) &&
            files.some((f) => (f?.name || "").toLowerCase().endsWith(".pptx"))
          body[hasPptxUpload ? "is_account_plan_upload" : "is_account_plan"] =
            true
        }
        if (pmbBriefId) {
          body.pmb_brief_id = pmbBriefId
        }
        if (pmbContext && typeof pmbContext === "object") {
          body.pmb_context = pmbContext
        }
        if (files?.length) {
          body.files = files
        }
        // Chip-click case — chat-bubble label override. Node persists
        // this into agent_session_chat_history.user_input.display_text
        // alongside the full prompt; v2-history-replay then prefers it
        // over .text for the user-bubble content on refresh.
        if (displayText) {
          body.display_text = displayText
        }

        // PMB v2 turn detection — used by the appendStep noise gate
        // below so that planner_error / executor_failed / critic / etc.
        // failure-steps never surface technical reasons in the chat. The
        // user-facing surface is just the canonical red error bubble.
        // Detected upfront here when the sender carried PMB v2 markers;
        // for free-text "generate a pre-meeting brief" typed cold in
        // chat, the flag is set retrospectively when a PMB v2 shaped
        // frame arrives (ui_component with the v2 resource_name OR an
        // ask_user with any pmb_*-namespaced clarification key).
        const isPmbV2Turn =
          transferToAgent === "GeneratePreMeetingBriefAgentV2" ||
          (pmbContext != null && typeof pmbContext === "object")

        setMessages(sessionId, {
          role: "assistant",
          content: "",
          id: assistantMessageId,
          isPending: true,
          agentSteps: [],
          askUser: null,
          _isPmbV2: isPmbV2Turn,
          _pmbForceDefault: Boolean(pmbForceDefault)
        })

        // v2 chat-agent endpoint — api-getorbitx forwards the py-processing
        // SSE stream verbatim. The browser reads frames ("event: X\ndata:
        // {...}\n\n") via the same getReader pattern as before, but the
        // frame shape is different from v1 so we parse whole frames (split
        // on \n\n) instead of line-by-line.
        const response = await fetch(
          withLinkedTenant(
            `${URLS.V1.replace("/v1", "/v2")}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${sessionId}`
          ),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo?.token}`
            },
            body: JSON.stringify(body),
            signal: abortController?.signal
          }
        )
        // Surface any non-2xx status as an error instead of silently trying
        // to parse a body that isn't SSE. Previously only 500 was caught,
        // and any 4xx slipped through to getReader() → no frames → stuck
        // `isPending` forever + fake ThoughtTimer animating indefinitely.
        if (!response.ok) {
          let serverMsg = ""
          try {
            serverMsg = await response.text()
          } catch (err) {
            // body read can fail for aborted responses (ENG-1167/1293
            // debug surface).
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] failed to read error response body",
              response.status,
              err
            )
          }
          throw new Error(
            `Request failed (${response.status})${serverMsg ? ": " + serverMsg.slice(0, 240) : ""}`
          )
        }
        if (!response.body) {
          throw new Error(t("os_agents.label_readable_stream_unsupported"))
        } else {
          // POST is committed (2xx + body present). Fire the caller's
          // hook BEFORE we start consuming the stream so any route
          // change they trigger (and its chunk-error reload, if a new
          // build is live) lands after the BE has already received the
          // user message.
          firePostCommitted()
          readerRef.current = response.body.getReader()
          const decoder = new TextDecoder("utf-8")
          let buf = ""
          if (!readerRef.current) return
          try {
            // eslint-disable-next-line no-constant-binary-expression
            while (true && isMountedRef.current) {
              const { done, value } = await readerRef.current.read()
              if (done) break
              buf += decoder.decode(value, { stream: true })
              // SSE frames are separated by a blank line (CRLF|LF pair).
              let idx: number
              while ((idx = buf.indexOf("\n\n")) !== -1) {
                const frame = buf.slice(0, idx)
                buf = buf.slice(idx + 2)
                if (frame.trim()) {
                  // DIAG: log each parsed SSE frame's event line so we
                  // can correlate with api-getorbitx forward.write logs.
                  // If forward.write shows 2 ui_component but this log
                  // shows 1, the FE reader (or browser) dropped a
                  // frame somewhere along the way.
                  try {
                    const ev = frame
                      .split("\n")
                      .find((l) => l.startsWith("event:"))
                    const id = frame
                      .split("\n")
                      .find((l) => l.startsWith("id:"))
                    // eslint-disable-next-line no-console
                    console.debug(
                      `[v2-sse] frame parsed ${id || "(no-id)"} ${ev || "(no-event)"}`
                    )
                  } catch (err) {
                    // best-effort debug log parsing — surface for
                    // ENG-1167/1293 visibility.
                    // eslint-disable-next-line no-console
                    console.error("[v2-debug] frame metadata parse failed", err)
                  }
                  processV2Frame(
                    sessionId,
                    frame,
                    assistantMessageId,
                    selectedAccount
                  )
                  // ENG-1325 follow-up: stamp the highest event_id
                  // processed onto the bubble so a later reattach can
                  // dedupe BE's event-id=0 replay (steps were doubling
                  // in the UI after switch-away → switch-back).
                  const sentEventId = _parseSseEventId(frame)
                  if (sentEventId !== null) {
                    setMessages(sessionId, {
                      id: assistantMessageId,
                      lastEventId: sentEventId
                    })
                  }
                }
              }
            }
            if (buf.trim()) {
              processV2Frame(
                sessionId,
                buf,
                assistantMessageId,
                selectedAccount
              )
              const trailEventId = _parseSseEventId(buf)
              if (trailEventId !== null) {
                setMessages(sessionId, {
                  id: assistantMessageId,
                  lastEventId: trailEventId
                })
              }
              buf = ""
            }
          } catch (err) {
            // stream interruption (network drop, abort, etc.). Surface
            // for ENG-1167/1293 — the silent swallow here is what hid
            // the trouble-serving errors from devtools.
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] stream read interrupted (sendMessage)",
              err
            )
          }
          // Stream ended. If the message is still pending, the server
          // closed the connection WITHOUT ever emitting a `done` frame —
          // either the worker crashed mid-response OR the pause-for-
          // approval path just completed (plan_proposed was the last
          // frame; py leaves the stream open in awaiting_user status
          // but our abort during resume closes it). The first case
          // warrants an error bubble; the second does NOT — the resume
          // handler is about to take over and stream into the same
          // message row.
          //
          // Distinguisher: `intentionalAbort` is set to true by
          // resumeWithDecision *before* it aborts the prior stream. If
          // it's true here, we're in the benign second case — skip the
          // error write. The resume handler will either produce the
          // final answer (success) or paint its own error (real
          // failure). Either way, NOT our job to pre-paint.
          const tailState =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ]
          // ENG-1325 guard: skip the error paint when this session has
          // been deactivated (user navigated to another chat or off the
          // route entirely). The BE worker keeps running and the route-
          // mount reattach hint picks it up on return. Without this
          // gate the prior chat shows a permanent failure bubble even
          // though the worker eventually finishes successfully.
          // Race-safe across React's cleanup→effect-body sequence —
          // see deactivatedSessions module-level comment.
          if (
            tailState &&
            (tailState as any).isPending &&
            !(tailState as any).askUser &&
            !intentionalAbort &&
            !deactivatedSessions.has(sessionId)
          ) {
            // Stream ended without `done` and the message is still
            // pending — surface for ENG-1167/1293 visibility. This is
            // the path that fires for "trouble serving your request"
            // when the BE closed the SSE early without a done frame.
            //
            // askUser gate: when the BE pauses for picker / plan-approval
            // it closes the SSE without a done frame and leaves the worker
            // parked in awaiting_user. The next user action opens a fresh
            // resume stream that emits the real done. Painting an error
            // bubble here would falsely flag that benign pause as a crash.
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] sendMessage stream ended without done frame — painting canonical error",
              {
                sessionId,
                assistantMessageId,
                tailStateKeys: Object.keys(tailState || {})
              }
            )
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              error: GENERIC_ERROR_MESSAGE
            })
          }
          // BEGIN_PITCH_DECK_FEATURE
          // Fallback: sales pitch V2 agent ran but ui_component frame never
          // arrived (py race condition OR stream crash). Placed after tailState
          // check so it fires on both normal end and crash. intentionalAbort
          // guards against user-cancel and plan-approval-abort paths.
          const resolvedFallbackOpportunity =
            opportunityIdOverride ?? selectedOpportunity
          if (
            isMountedRef.current &&
            pitchDeckV2SeenRef.current &&
            !pitchDeckV2SplitterSetRef.current &&
            resolvedFallbackOpportunity &&
            !intentionalAbort
          ) {
            const fallbackContent = {
              resourceName: "SalesPitchDeckBuilderV2",
              resourceParams: {
                mode: "generate",
                opportunity_id: resolvedFallbackOpportunity,
                account_id: pitchDeckV2AccountIdRef.current || undefined,
                account_name: "",
                opportunity_name: ""
              }
            }
            setShowSplitter(fallbackContent)
            setMessages(sessionId, {
              id: assistantMessageId,
              content: fallbackContent,
              content_type: "ui_component",
              isPending: false
            })
          }
          // END_PITCH_DECK_FEATURE
        }
      } catch (err: any) {
        // Either the POST never resolved (network error) or it 4xx/5xx'd
        // and we threw above. Either way the caller's pending navigate
        // can fire now — they need to render the error bubble in the
        // session route rather than orphan it on the empty agent page.
        // No-op if already fired in the success path.
        firePostCommitted()
        // ENG-1167/1293: the bubble below loses the technical specifics
        // (status / exception name / message). Surface them here so the
        // "trouble serving your request" mystery is debuggable from the
        // browser console alone.
        // eslint-disable-next-line no-console
        console.error("[v2-debug] sendMessage failed", {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
          err
        })
        if (err?.name === "AbortError") {
          if (intentionalAbort) {
            // The resume handler aborted us to take over this turn — the
            // user is answering a clarification, not cancelling. Do NOT
            // paint the message as errored. The resume's _consumeV2Stream
            // continues writing frames into the same assistantMessageId,
            // so the UI keeps moving forward.
            intentionalAbort = false
          } else if (!deactivatedSessions.has(sessionId)) {
            // User cancelled by sending a new message mid-stream in
            // THIS session (component still active). DO NOT set
            // `content: ""` — the store merges partials into the
            // existing record, so that would wipe any already-
            // streamed answer. Only flip off pending + flag the
            // error. Canonical bubble for consistency with every
            // other failure mode.
            // ENG-1325: when this session has been deactivated (user
            // switched chats or off the route), skip the error paint
            // and let the reattach path pick this turn up on return.
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              error: GENERIC_ERROR_MESSAGE
            })
          }
        } else if (!deactivatedSessions.has(sessionId)) {
          // Any other error — HTTP 500/4xx, network unreachable, fetch
          // kill from a dead socket — all surface as the same canonical
          // bubble. The technical specifics (status code, exception
          // name, message) are lost here intentionally; they live in
          // the worker logs under the x-request-id we forwarded.
          // ENG-1325: silenced when this session has been deactivated
          // so cross-chat navigation doesn't strand the prior session
          // with a phantom error.
          setMessages(sessionId, {
            role: "assistant",
            id: assistantMessageId,
            isPending: false,
            agentSteps: [],
            askUser: null,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } finally {
        readerRef.current?.cancel()
        readerRef.current = null
        // CRITICAL: do NOT call `abortController.abort()` in finally.
        //
        // sendMessage and resumeWithDecision share the module-level
        // `abortController` variable. When the user clicks Submit on an
        // ask_user prompt, resumeWithDecision aborts our send and
        // installs a FRESH abortController for its own fetch. Our inner
        // reader catch swallows the AbortError, we exit the try, and
        // `finally` runs. If `finally` aborted the current
        // `abortController`, it would abort the RESUME's fetch — which
        // is exactly the "signal is aborted without reason" bubble the
        // user was seeing. Just detach OUR reference; whoever owns the
        // live controller now is responsible for its lifecycle.
        //
        // Identity-check before nulling out: if `abortController` has
        // already been reassigned to a newer controller (e.g., by
        // resumeWithDecision or by a follow-up sendMessage call), leave
        // it alone — clearing it would confuse the owner's top-level
        // `if (abortController) abortController.abort()` guard.
        if (abortController === myAbortController) {
          abortController = null
        }
        setPendingMessage("")
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenInfo]
  )

  /**
   * v2 SSE frame parser.
   *
   * The py-processing-api-server emits whole SSE frames in the form:
   *   id: 42
   *   event: plan_step_completed
   *   data: {"step_id": "s1", "title": "...", "text": "..."}
   *
   * We split the network buffer on "\n\n" before calling this, so `frame`
   * contains one full frame. Each event type updates the in-flight
   * assistant message via `setMessages(sessionId, {id, ...partial})`,
   * which MERGES the partial into the existing record (store contract).
   *
   * Event lifecycle:
   *   accepted / connected → loader keeps spinning; no step row
   *   plan_proposed        → add "plan ready" step + askUser {approve|cancel}
   *   plan_auto_executing  → add "auto-executing" step (rare under current thresholds=0)
   *   plan_step_started    → append running step
   *   plan_step_completed  → mark matching started step done + append a
   *                          "complete" row (2-frames-per-step per user
   *                          preference "more comm, the merrier")
   *   synth_started/done   → similar pair
   *   synth_delta          → append delta to message.content.parts[0]
   *                          (live typewriter render — critic was
   *                          removed June 2026 so no quality gate
   *                          holds tokens back)
   *   ask_user             → prompt row + askUser {clarifications} options
   *   done                 → final_answer → content (renderer reads content.parts[0])
   *   error                → error field on the message
   */
  const processV2Frame = useCallback(
    async (
      sessionId: string,
      frame: string,
      assistantMessageId: string,
      accountId?: string
    ) => {
      let eventType = "message"
      let dataStr = ""
      let sseId: number | null = null
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) eventType = line.slice(6).trim()
        else if (line.startsWith("data:"))
          dataStr += (dataStr ? "\n" : "") + line.slice(5).trimStart()
        else if (line.startsWith("id:")) {
          const n = parseInt(line.slice(3).trim(), 10)
          if (!Number.isNaN(n)) sseId = n
        }
      }
      if (!dataStr) return
      let data: any
      try {
        data = JSON.parse(dataStr)
      } catch (err) {
        // SSE frame body wasn't valid JSON. Skip but log for
        // ENG-1167/1293 — malformed frames could be the upstream cause.
        // eslint-disable-next-line no-console
        console.error("[v2-debug] SSE frame JSON parse failed", err, {
          dataStr: dataStr.slice(0, 240)
        })
        return
      }
      const ord = sseId !== null ? sseId : Date.now()

      // Helper — immutably merge a new step into the current `agentSteps`
      // of the assistant message.
      //
      // PMB v2 noise gate: when this turn is a PMB v2 turn AND the step
      // is a failed-status step (planner_error, executor_failed,
      // plan_step_completed[failed], pipeline error, etc.), we
      // suppress the step from chat entirely and
      // surface just the canonical red error bubble instead. The widget
      // path already handles its own error UX; chat shouldn't leak
      // internal failure-mode names ("v2 chat-agent enqueue failed",
      // "planner ValidationError", section tracebacks) to the rep.
      // Other v2 chat flows (sales pitch, PPT, RFP) keep the verbose
      // step log — only PMB v2 gets the gate.
      const appendStep = (step: any) => {
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        if ((cur as any)?._isPmbV2 && step?.status === "failed") {
          // PMB v2 failure path: hide the noisy failure step but DO still
          // terminate any in-progress step (e.g. "Processing your request")
          // so its spinner stops alongside the red error bubble.
          const arr = [...(((cur as any).agentSteps as any[]) || [])]
          let mutated = false
          for (let i = 0; i < arr.length; i++) {
            if (arr[i].status === "running") {
              arr[i] = { ...arr[i], status: "failed" }
              mutated = true
            }
          }
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            askUser: null,
            error: GENERIC_ERROR_MESSAGE,
            ...(mutated ? { agentSteps: arr } : {})
          })
          return
        }
        setMessages(sessionId, {
          id: assistantMessageId,
          agentSteps: [...((cur as any).agentSteps || []), step]
        })
      }

      // Patch the running chip matching `stepId` with `detail`, leaving
      // status as "running". Used by the `heartbeat` SSE event so long-
      // running steps show a friendly "Still working on …" sub-line
      // instead of a silent dot animation. No-op when no running chip
      // matches (e.g. the heartbeat lands after the step finished).
      const setRunningDetailByStepId = (stepId?: string, detail?: string) => {
        if (!stepId || !detail) return
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        const arr = [...(((cur as any).agentSteps as any[]) || [])]
        for (let i = arr.length - 1; i >= 0; i--) {
          if (
            arr[i].status === "running" &&
            arr[i].stepId === stepId &&
            typeof arr[i].eventType === "string" &&
            arr[i].eventType.startsWith("plan_step_started")
          ) {
            arr[i] = { ...arr[i], detail }
            break
          }
        }
        setMessages(sessionId, { id: assistantMessageId, agentSteps: arr })
      }

      const markLastRunningByPrefix = (prefix: string, detail?: string) => {
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        const arr = [...(((cur as any).agentSteps as any[]) || [])]
        for (let i = arr.length - 1; i >= 0; i--) {
          if (
            arr[i].status === "running" &&
            typeof arr[i].eventType === "string" &&
            arr[i].eventType.startsWith(prefix)
          ) {
            arr[i] = { ...arr[i], status: "done", ...(detail && { detail }) }
            break
          }
        }
        setMessages(sessionId, { id: assistantMessageId, agentSteps: arr })
      }

      // Backstop invoked when synth_started fires — by then ALL plan
      // steps must have completed (executor invariant), so any
      // plan_step_started row still in `running` indicates a missed
      // plan_step_completed event. Flip them all to done.
      // Also catches plan_step_fallback_triggered rows that were
      // appended as "running" — their parent step's completion is
      // implicit by the time synth runs.
      const markAllRunningStepsDoneOnSynthStart = () => {
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        const arr = [...(((cur as any).agentSteps as any[]) || [])]
        let mutated = false
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].status !== "running") continue
          const et =
            typeof arr[i].eventType === "string" ? arr[i].eventType : ""
          if (
            et.startsWith("plan_step_started") ||
            et === "plan_step_fallback_triggered" ||
            et === "plan_step_substep"
          ) {
            arr[i] = { ...arr[i], status: "done" }
            mutated = true
          }
        }
        if (mutated) {
          setMessages(sessionId, { id: assistantMessageId, agentSteps: arr })
        }
      }

      // When a parent plan_step_completed lands, flip ALL still-
      // running plan_step_substep rows for that stepId to "done".
      // Without this the chunk-progress rows ("Processing accounts
      // 1-200 of 1001 (chunk 1/6)") keep spinning forever even after
      // the sweep is finished — observed RCA: invocation 0a895350.
      // Sweep agent doesn't emit a per-chunk "completed" event; the
      // parent's plan_step_completed is the right anchor.
      const markSubstepsDoneForStep = (stepId?: string) => {
        if (!stepId) return
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        const arr = [...(((cur as any).agentSteps as any[]) || [])]
        let mutated = false
        for (let i = 0; i < arr.length; i++) {
          if (
            arr[i].eventType === "plan_step_substep" &&
            arr[i].stepId === stepId &&
            arr[i].status === "running"
          ) {
            arr[i] = { ...arr[i], status: "done" }
            mutated = true
          }
        }
        if (mutated) {
          setMessages(sessionId, { id: assistantMessageId, agentSteps: arr })
        }
      }

      // Flip every still-spinning step to "failed" when an error /
      // plan_failed event lands. Without this, the step the pipeline
      // was on when the abort happened keeps its loader animation
      // forever (observed: invocation b8e90060 — "Build the Data
      // Analytics deck" stuck with the dot animation after a
      // pipeline_error event arrived). Both the parent
      // plan_step_started AND its plan_step_substep children are
      // flipped so no spinner survives.
      const markAllRunningAsFailed = (failureDetail?: string) => {
        const cur =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ] || {}
        const arr = [...(((cur as any).agentSteps as any[]) || [])]
        let mutated = false
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].status === "running") {
            arr[i] = {
              ...arr[i],
              status: "failed",
              ...(failureDetail && !arr[i].detail
                ? { detail: failureDetail }
                : {})
            }
            mutated = true
          }
        }
        if (mutated) {
          setMessages(sessionId, { id: assistantMessageId, agentSteps: arr })
        }
      }

      switch (eventType) {
        case "accepted":
        case "connected":
        case "reattached":
          // `reattached` is emitted by api-getorbitx's re-attach
          // endpoint as the start frame so the FE can confirm the
          // proxy hand-off. Same shape as `connected` — capture the
          // invocation_id, render nothing.
          if (data?.invocation_id) {
            setMessages(sessionId, {
              id: assistantMessageId,
              invocationId: data.invocation_id
            })
          }
          return

        case "plan_step_skipped":
          // Cascade-fallback step whose upstream succeeded (the "if
          // needed" path wasn't needed). Internal plumbing — no chip,
          // no spinner to flip.
          return

        case "plan_step_blocked":
          // Two cases land here:
          //  (a) Hard dep failed — step never started, nothing to flip.
          //  (b) Sibling paused for user-confirmation while THIS step
          //      was still in flight (transient=true). BE killed it
          //      mid-execution without a completed/failed frame (ADK
          //      ParallelAgent abandons siblings silently on early-exit),
          //      so the matching running chip would spin forever.
          // Flip any matching running chip to "done"; markLastRunning
          // ByPrefix is a no-op when no running chip is found, so (a)
          // stays a silent no-op. Also drop any substep spinners.
          markLastRunningByPrefix("plan_step_started", "")
          if (data?.step_id) markSubstepsDoneForStep(data.step_id)
          return

        case "progress":
          // Worker-emitted intermediate progress while the runner is
          // booting up (session seed, resume apply, planner LLM call).
          // Bridges the 20-40s gap between connected and the first real
          // pipeline event so the UI never sits on a bare spinner.
          appendStep({
            id: ord,
            eventType,
            title:
              data?.message ||
              t("os_agents.label_progress_stage", {
                stage: data?.stage || t("os_agents.label_progress_working")
              }),
            status: "done",
            stepId: data?.stage,
            raw: data
          })
          return

        case "heartbeat":
          // Periodic "Still working on …" hint from the BE when a
          // step is running long. Replaces the silent SSE keep-alive
          // comment with something meaningful: surfaces the running
          // step's title under its chip so the user has visible
          // assurance that work is progressing. Does NOT add a new
          // chip — patches the existing running chip's detail.
          // No-op when no matching running chip exists (heartbeat
          // raced past the step's completion).
          setRunningDetailByStepId(data?.step_id, data?.message)
          return

        case "plan_proposed": {
          // Build a rich detail: rationale + numbered step titles so the
          // plan row stays meaningful in the history even after approval
          // (when the askUser approval card disappears, this row is the
          // only lasting record of what the plan was going to do).
          const proposedSteps = (data?.plan?.steps || []) as Array<{
            id: string
            title?: string
            description?: string
          }>
          const stepList = proposedSteps
            .map((s, i) => `${i + 1}. ${s.title || s.id}`)
            .join("\n")
          const detailParts: string[] = []
          if (data?.rationale) detailParts.push(data.rationale)
          if (stepList) detailParts.push(`Steps:\n${stepList}`)
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_plan_ready_awaiting_approval", {
              count: proposedSteps.length
            }),
            status: "done",
            detail: detailParts.join("\n\n"),
            raw: data
          })
          setMessages(sessionId, {
            id: assistantMessageId,
            askUser: {
              source: "plan_approval",
              prompt:
                data?.rationale || "Review the plan and approve to proceed.",
              plan: data?.plan,
              options: [
                { value: "approve", label: "Approve & run" },
                { value: "cancel", label: "Cancel" }
              ]
            },
            isPending: true
          })
          return
        }

        case "plan_auto_executing":
          // No detail. The `rationale` field is the planner LLM's
          // free-form explanation ("No problem. I won't share raw
          // queries…", "I'll prepare your pre-meeting brief tailored
          // to a renewal conversation…"). When the planner is refusing
          // an out-of-scope request or rationalising scope choices,
          // its rationale belongs in the final answer below the
          // timeline — not in a chip detail that contradicts the
          // chip's "Auto-executing plan" title (RCA 2026-05-15: user
          // saw "Auto-executing plan" + "I won't share raw queries"
          // — confusing pairing). The numbered step list that follows
          // already conveys what's about to run.
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_auto_executing_plan"),
            status: "done",
            raw: data
          })
          return

        case "plan_step_started":
          appendStep({
            id: ord,
            eventType,
            title:
              data?.title ||
              t("os_agents.label_step_started_fallback", {
                stepId: data?.step_id || "?"
              }),
            status: "running",
            stepId: data?.step_id,
            raw: data
          })
          return

        case "plan_step_completed": {
          // Pass "" so the in-flight chip flips to done WITHOUT inheriting
          // the full step text as detail. The helper uses `detail && {
          // detail }`, so an empty string skips the write. The step text
          // is the agent's full deliverable (often 17–130 KB of markdown
          // tables); even truncated to 280 chars it leaks a partial
          // table-header row into the timeline chip and looks broken.
          // The rich content is in the synth answer below; the timeline
          // only needs "step X — complete".
          markLastRunningByPrefix("plan_step_started", "")
          // Also flip any "Trying backup lookup" chip — when the step
          // first emitted plan_step_fallback_triggered (running chip)
          // and later recovered via web fallback, the started-chip
          // flip above misses the fallback chip. Without this second
          // call the spinner persists forever next to "Trying backup
          // lookup (web search)". RCA: invocation 3d1fb440 (Issue 1
          // in 2026-05-13 audit) — fallback recovered at +30s but the
          // chip kept spinning until the step ended in ask_user.
          markLastRunningByPrefix("plan_step_fallback_triggered", "")
          // Stop the chunk spinners — see markSubstepsDoneForStep.
          markSubstepsDoneForStep(data?.step_id)
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_step_complete_suffix", {
              title: data?.title || t("os_agents.label_step")
            }),
            status: "done",
            stepId: data?.step_id,
            raw: data
          })
          return
        }

        case "plan_step_failed": {
          // BE emits this when a step's tool returned non-ok and the
          // websearch fallback also couldn't recover. Without an
          // explicit handler the FE would dump raw JSON. Flip the
          // matching plan_step_started row to failed (red icon) so the
          // user sees the step finished with an error. The follow-up
          // ask_user / error frame surfaces the recovery UI.
          //
          // Match BOTH plan_step_started AND plan_step_fallback_triggered
          // running chips for this step — same reason as in
          // plan_step_completed: when fallback was tried and also
          // failed, both chips are running and need terminal state.
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}
          const arr = [...(((cur as any).agentSteps as any[]) || [])]
          for (let i = arr.length - 1; i >= 0; i--) {
            if (
              arr[i].status === "running" &&
              typeof arr[i].eventType === "string" &&
              (arr[i].eventType.startsWith("plan_step_started") ||
                arr[i].eventType.startsWith("plan_step_fallback_triggered"))
            ) {
              arr[i] = { ...arr[i], status: "failed" }
              // don't break — both rows may be running for this step
            }
          }
          setMessages(sessionId, {
            id: assistantMessageId,
            agentSteps: arr
          })
          // Also flip any in-flight chunk substep chips for this step
          // (BatchedAccountSweepAgent emits plan_step_substep rows
          // per chunk; on failure they kept spinning forever — RCA
          // inv 476543b0, 2026-05-13). Mirrors the call we already
          // make on plan_step_completed.
          if (data?.step_id) markSubstepsDoneForStep(data.step_id)
          return
        }

        case "plan_step_substep": {
          // Cross-account sweep progress — emitted per chunk by
          // BatchedAccountSweepAgent. Appears as a nested status row
          // under the parent plan_step_started entry. status="running"
          // so the indicator spins; markSubstepsDoneForStep flips
          // them to "done" when the parent's plan_step_completed
          // arrives.
          const idx = Number(data?.substep_idx) || 0
          const total = Number(data?.substep_total) || 0
          const rowsSoFarRaw = data?.rows_so_far
          const rowsSoFar =
            typeof rowsSoFarRaw === "number" && rowsSoFarRaw >= 0
              ? rowsSoFarRaw
              : null
          // Sweep agent emits rows_so_far=-1 for parallel chunks
          // because cumulative count isn't meaningful when chunks
          // run concurrently. Hide the row count in that case
          // instead of rendering "-1 rows so far" (looks like a bug).
          const detailParts: string[] = []
          if (total > 0)
            detailParts.push(
              t("os_agents.label_chunk_of_total", { idx, total })
            )
          if (rowsSoFar !== null)
            detailParts.push(
              t("os_agents.label_rows_so_far", { count: rowsSoFar })
            )
          // The BE-supplied title is preferred — it's a richer
          // sentence ("Processing accounts 1-200 of 1001 (chunk
          // 1/6)") that the planner already curated. Fallback only
          // fires if BE forgot the title; locale-keyed for safety.
          const fallbackTitle = total
            ? t("os_agents.label_sweep_chunk", { idx, total })
            : t("os_agents.label_sweep_chunk_short", { idx })
          appendStep({
            id: ord,
            eventType,
            title: data?.title || fallbackTitle,
            status: "running",
            stepId: data?.step_id,
            detail: detailParts.length ? detailParts.join(" · ") : undefined,
            raw: data
          })
          return
        }

        case "synth_started": {
          // Timeline-only signal: synth started running. content.parts[0]
          // remains empty until the first synth_delta lands. The FE
          // now renders tokens live (typewriter feel) — critic was
          // removed June 2026, so there's no quality gate holding the
          // answer back.
          //
          // BACKSTOP for stuck plan_step_started rows: synth runs ONLY
          // after all plan steps complete (executor invariant). So if
          // any plan_step_started row is still in "running" state when
          // synth_started fires, plan_step_completed went missing for
          // that step. Flip every still-running plan_step_started (and
          // its fallback_triggered children) to "done" here — better
          // to silently terminate a spinner than leave it forever.
          markAllRunningStepsDoneOnSynthStart()
          // DEFENCE-IN-DEPTH against double-streaming: reset content.parts[0]
          // to "" whenever a NEW synth_started fires. The synth_delta
          // handler appends each delta to content.parts[0], so without
          // this reset a second streamer in the same turn would
          // concatenate onto the first one's text (observed when a
          // sub-agent emitted synth_delta during a step and synth then
          // ran on top of it — RCA Acuity Brands tech stack, 2026-06-03,
          // BE fix in StreamingToolAgent._should_stream_to_user closed
          // the trigger). This guard ensures the FE stays correct if
          // anything in the BE ever produces two synth_started frames
          // in a single turn again. Idempotent on the first synth_started
          // of a turn — content was empty anyway.
          {
            const cur =
              useOsAgentsIdStore.getState().messages?.[sessionId]?.[
                assistantMessageId
              ] || {}
            const existingContent: any = (cur as any)?.content || {}
            const hasParts =
              Array.isArray(existingContent.parts) &&
              existingContent.parts.length > 0
            if (hasParts && existingContent.parts[0]) {
              const hasCards =
                Array.isArray(existingContent.cards) &&
                existingContent.cards.length > 0
              const isUiComponentTurn =
                (cur as any)?.content_type === "ui_component" || hasCards
              setMessages(sessionId, {
                id: assistantMessageId,
                role: "assistant",
                content: {
                  ...existingContent,
                  parts: [""]
                },
                content_type: isUiComponentTurn ? "ui_component" : "text",
                pendingSynthText: undefined
              } as any)
            }
          }
          appendStep({
            id: ord,
            eventType,
            title: data?.title || t("os_agents.label_compiling_final_answer"),
            status: "running",
            raw: data
          })
          return
        }

        case "synth_delta": {
          // Live token stream — append the delta to content.parts[0]
          // so the user sees the answer being typed out. Critic is
          // gone (June 2026) so there's no reason to hold these back.
          const delta = typeof data?.text === "string" ? data.text : ""
          if (!delta) return
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}
          const existingContent: any = (cur as any)?.content || {}
          const currentText =
            Array.isArray(existingContent.parts) &&
            typeof existingContent.parts[0] === "string"
              ? (existingContent.parts[0] as string)
              : ""
          const hasCards =
            Array.isArray(existingContent.cards) &&
            existingContent.cards.length > 0
          const isUiComponentTurn =
            (cur as any)?.content_type === "ui_component" || hasCards
          setMessages(sessionId, {
            id: assistantMessageId,
            role: "assistant",
            content: {
              ...existingContent,
              parts: [currentText + delta]
            },
            content_type: isUiComponentTurn ? "ui_component" : "text"
          } as any)
          return
        }

        case "synth_completed": {
          // Finalize: overwrite content.parts[0] with the canonical
          // full text from the BE (in case any synth_delta was missed
          // or dropped in transit), then flip the "Compiling final
          // answer" chip to done. No critic_verdict event follows.
          const finalText = typeof data?.text === "string" ? data.text : ""
          if (finalText) {
            const cur =
              useOsAgentsIdStore.getState().messages?.[sessionId]?.[
                assistantMessageId
              ] || {}
            const existingContent: any = (cur as any)?.content || {}
            const hasCards =
              Array.isArray(existingContent.cards) &&
              existingContent.cards.length > 0
            const isUiComponentTurn =
              (cur as any)?.content_type === "ui_component" || hasCards
            setMessages(sessionId, {
              id: assistantMessageId,
              role: "assistant",
              content: {
                ...existingContent,
                parts: [finalText]
              },
              content_type: isUiComponentTurn ? "ui_component" : "text",
              // Clear any legacy stash that may have been written by
              // an older session's replay path.
              pendingSynthText: undefined
            } as any)
          }
          markLastRunningByPrefix("synth_started", "")
          appendStep({
            id: ord,
            eventType,
            title: data?.title || t("os_agents.label_final_answer_compiled"),
            status: "done",
            raw: data
          })
          return
        }

        case "synth_skipped":
          // Internal plumbing: executor chose to use the last step's
          // text verbatim as the final answer (e.g. UI-producing
          // agent returned no card, so synth would fabricate). The
          // user sees the step text as the answer; there's nothing
          // useful to surface here. Silent no-op — without this it
          // falls through to the default case and dumps the raw
          // `{reason, agent}` JSON as a chip detail.
          return

        case "critic_verdict":
          // Critic was removed June 2026. BE no longer emits this.
          // Silent no-op kept only to swallow the event when replaying
          // an old session whose stored event stream still includes
          // critic_verdict frames (no chip, no promote — content is
          // already rendered live via synth_delta + synth_completed).
          return

        case "ask_user": {
          // Render a human-readable step detail (question text bullets)
          // instead of dumping raw clarifications JSON. The live form
          // below (askUser.clarifications) is the interactive control;
          // this step row is the persistent history entry that lives on
          // even after the user answers. Keeping it scannable matters.
          const askClars = Array.isArray(data?.clarifications)
            ? (data.clarifications as Array<{
                question?: string
                key?: string
              }>)
            : []
          // Retrospective PMB v2 detection — when the planner emits a
          // clarification with PMB-v2-namespaced keys, this turn is a
          // PMB v2 flow even if the FE didn't pass transferToAgent
          // upfront (free-text "generate a pre-meeting brief"
          // cold-start). Flip the message's _isPmbV2 flag so any later
          // failure-step gets gated by appendStep.
          // pmb_q2 was dropped from the picker on 2026-05-19 — match any
          // pmb_*-namespaced key so further question reshuffles don't break
          // this retrospective detection. Stays consistent with the picker-
          // detection helper in agent-steps-ui-v2.tsx.
          const isPmbV2Clarification = askClars.some((c) =>
            c?.key?.startsWith("pmb_")
          )
          if (isPmbV2Clarification) {
            setMessages(sessionId, {
              id: assistantMessageId,
              _isPmbV2: true
            })
          }
          // Chip detail strategy:
          //   - If clarifications exist, show their numbered questions.
          //   - Else, prefer `awaiting.step_text_preview` (the friendly
          //     "we don't have X on file" sentence from the BE).
          //   - Never leak `awaiting: <source>` (internal jargon like
          //     "execution_failure"). RCA inv cc457f30 (2026-05-13):
          //     the chip showed `awaiting: execution_failure` which
          //     means nothing to a user.
          // Flip any in-flight `plan_step_started` chip to done. When a
          // sub-agent pauses on ask_user, the BE does NOT emit a
          // `plan_step_completed` (the step paused, didn't finish), so
          // without this the step row's spinner sits running forever
          // alongside the "Waiting on you" chip below — looks broken.
          // RCA inv d7482b50-5021 (2026-05-15): PMB v2 paused on
          // ask_user, "Preparing your pre-meeting brief" stayed
          // spinning even though the user-facing form was rendered.
          // Same pattern markAllRunningStepsDoneOnSynthStart uses;
          // wrapping fallback chips inherit closure via
          // markLastRunningByPrefix("plan_step_fallback_triggered").
          markLastRunningByPrefix("plan_step_started", "")
          markLastRunningByPrefix("plan_step_fallback_triggered", "")
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_waiting_on_you"),
            status: "done",
            // No `detail` here: the live form below carries the
            // question text. Keeping detail empty avoids duplicate
            // text + inconsistent typography between chip and form.
            raw: data
          })
          // Preserve the FULL clarifications array (previous bug: we only
          // rendered clarifications[0], silently dropping the second+ and
          // ignoring answer_type semantics — so a 3-of-N multi_choice was
          // treated as a single click-to-submit). The UI component handles
          // rendering per answer_type + aggregation. We still support
          // legacy single-option prompts (approve/cancel on plan_proposed)
          // via the `options` top-level field.
          //
          // BE ships the prompt at one of three locations depending on
          // event origin:
          //   (1) top-level `prompt` — in-plan step_confirmation gates
          //   (2) `awaiting.prompt` — execution-time ask_user_tool /
          //       ask_user-classifier triggers, PMB v2 tool's "could
          //       not resolve person" pleas
          //   (3) `awaiting.step_text_preview` — friendly BE failure
          //       explanation (e.g. "We don't have any offerings on
          //       file…") when no explicit prompt exists. RCA inv
          //       cc457f30 (2026-05-13).
          // Read all three so we never silently fall back to "Please
          // respond" when the agent has actually written something
          // specific.
          //
          // For agent-issued ask_user with ONLY `awaiting.prompt` and no
          // clarifications array (PMB v2 cold-start pattern), we also
          // synthesize a single free-text clarification so AskUserCard
          // renders a usable input + submit button instead of an empty
          // form.
          const beTopPrompt =
            typeof data?.prompt === "string" && data.prompt ? data.prompt : ""
          const beAwaitingPrompt =
            typeof data?.awaiting?.prompt === "string" && data.awaiting.prompt
              ? data.awaiting.prompt
              : ""
          const beStepPreview =
            typeof data?.awaiting?.step_text_preview === "string" &&
            data.awaiting.step_text_preview
              ? data.awaiting.step_text_preview
              : ""
          const beClarifications = Array.isArray(data?.clarifications)
            ? data.clarifications
            : []
          const beOptions = Array.isArray(data?.options) ? data.options : []
          const isFreeFormAgentAsk =
            beClarifications.length === 0 &&
            beOptions.length === 0 &&
            !!beAwaitingPrompt
          const effectivePrompt =
            beTopPrompt ||
            beAwaitingPrompt ||
            beStepPreview ||
            (beClarifications.length
              ? t("os_agents.label_please_answer_following")
              : t("os_agents.label_please_respond_continue"))
          // `question` is left empty on the synthesized clarification —
          // the prompt is already rendered as the AskUserCard's `<p>`
          // heading (with `whitespace-pre-wrap` so multi-line agent
          // questions like "Which VP at ABB?\n\n1. …\n2. …" keep their
          // formatting). Surfacing the same string again as the input's
          // `<label>` produced a visible duplicate (ENG-1254): the
          // bordered prompt at the top, then the same text in bold
          // collapsed under it. ClarificationControl gates the `<label>`
          // on `c.question` truthiness, so an empty string renders the
          // input field cleanly with no label.
          const effectiveClarifications = isFreeFormAgentAsk
            ? [
                {
                  key: "answer",
                  question: "",
                  answer_type:
                    (data?.awaiting?.answer_type as string) || "text",
                  default: (data?.awaiting?.default as string) || ""
                }
              ]
            : beClarifications
          setMessages(sessionId, {
            id: assistantMessageId,
            askUser: {
              source: data?.awaiting?.source || "execution_ask_user",
              prompt: effectivePrompt,
              clarifications: effectiveClarifications,
              plan: data?.plan
            },
            isPending: true
          })
          return
        }

        case "cards_ready": {
          // NEW (2026-05-16): atomic multi-card delivery. The BE emits
          // a single `cards_ready` event containing the FULL ordered
          // cards array — replacing the N-separate-`ui_component`-events
          // approach which exposed an inter-frame abort race that
          // dropped the second card. Single frame = single setMessages
          // = no race window.
          //
          // BE-side also still emits per-leaf `ui_component` events
          // AFTER this one (for history-replay backward compat). When
          // `cards_ready` is seen, we mark a guard so the subsequent
          // `ui_component` handler is a no-op for this turn.
          const incomingCards: any[] = Array.isArray(data?.cards)
            ? data.cards
            : []
          const cards = incomingCards.map((c: any, i: number) => ({
            stepId: c?.step_id || "",
            resourceName: c?.resource_name,
            // Same per-turn remount token as the `ui_component` path below.
            resourceParams: {
              ...c?.params,
              _iterationToken: `${assistantMessageId}:${c?.step_id || i}`
            }
          }))
          if (cards.length === 0) {
            // eslint-disable-next-line no-console
            console.debug("[v2-sse] cards_ready: empty cards array")
            return
          }
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}
          const existingContent: any = (cur as any)?.content || {}
          const firstCard = cards[0]
          const cardContent = {
            parts: existingContent.parts || [""],
            resourceName: firstCard.resourceName,
            resourceParams: firstCard.resourceParams,
            cards,
            // Match the message-list key for the first card so the auto-opened
            // preview highlights its card (ENG-1905 highlight edge case).
            _previewKey: getCardPreviewKey(assistantMessageId, firstCard, 0)
          }
          // eslint-disable-next-line no-console
          console.debug(
            `[v2-sse] cards_ready merged ${cards.length} card(s) — ` +
              `steps=[${cards.map((c) => c.stepId).join(",")}]`
          )
          setMessages(sessionId, {
            id: assistantMessageId,
            role: "assistant",
            content_type: "ui_component",
            content: cardContent,
            isPending: false,
            askUser: null
          })
          // Auto-open splitter for the FIRST card — same as the old
          // single-card behavior.
          setShowSplitter(cardContent)
          if (cardContent.resourceName === "SalesPitchDeckBuilderV2") {
            pitchDeckV2SplitterSetRef.current = true
          }
          // Mark this turn's bubble as having consumed cards_ready so
          // the legacy ui_component events that follow are no-op'd.
          ;(window as any).__v2CardsReadyConsumed =
            (window as any).__v2CardsReadyConsumed || new Set<string>()
          ;((window as any).__v2CardsReadyConsumed as Set<string>).add(
            `${sessionId}:${assistantMessageId}`
          )
          return
        }

        case "ui_component": {
          // Legacy per-card path — when `cards_ready` already populated
          // the cards for this bubble, skip. This event still flows
          // through chatbot-v2.js tee for history persistence regardless.
          const consumedSet = (window as any).__v2CardsReadyConsumed as
            | Set<string>
            | undefined
          if (
            consumedSet &&
            consumedSet.has(`${sessionId}:${assistantMessageId}`)
          ) {
            // eslint-disable-next-line no-console
            console.debug(
              `[v2-sse] ui_component skipped — cards_ready already populated bubble`
            )
            return
          }
          // Backend emits one ui_component event per LEAF step that
          // produced a card. Synth runs FIRST and writes content.parts[0]
          // (the intro text rendered above the card stack); ui_component
          // events arrive AFTER and append to content.cards.
          //
          // Single-card plans (most common — single-step priorities /
          // PMB / news / etc.): one ui_component event → content.cards
          // has 1 entry → renderer still works.
          //
          // Multi-card plans (fan-out across N accounts where each leaf
          // is a UI-producing agent): N ui_component events → cards
          // stack in step-ID order.
          //
          // Backward compat: keep resourceName / resourceParams set to
          // the FIRST card so existing single-card renderers still work
          // if the agent-id-messages-v2 hasn't been updated yet.
          //
          // Shape note: the drawer (agent-right-panel.tsx) reads
          // `showSplitter?.resourceParams?.data`. ToolName's onClick
          // does `setShowSplitter(content)`, so `content.resourceParams`
          // must carry the params dict — putting params at the top
          // level of content (spreading) leaves the drawer empty.
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}

          // Retrospective PMB v2 marker — once we see a PMB v2 widget
          // frame, any subsequent failure-step on this turn should be
          // gated by the noise filter in appendStep.
          if (data?.resource_name === "GeneratePreMeetingBriefAgentV2") {
            setMessages(sessionId, {
              id: assistantMessageId,
              _isPmbV2: true
            })
          }

          // PMB v2 empty / error suppression — when the agent's tool
          // bailed (status="error" + no sections), the BE still emits a
          // ui_component frame (so the synthesizer/critic loop skips)
          // but there's nothing meaningful to render in the right panel.
          // Surface the actual reason in the canonical red error bubble
          // and skip setShowSplitter — keeps the "PMB v2 → widget"
          // mental model intact for success while not opening an empty
          // drawer on failure.
          const params = data?.params || {}
          const isPmbV2Empty =
            data?.resource_name === "GeneratePreMeetingBriefAgentV2" &&
            params?.status === "error" &&
            Array.isArray(params?.section_order) &&
            params.section_order.length === 0 &&
            Array.isArray(params?.errors) &&
            params.errors.length > 0
          if (isPmbV2Empty) {
            // Canonical user-facing error message for ALL PMB v2 errors.
            // params.errors[0] from py side is intentionally NOT surfaced
            // — the rep should never see internal reasons like
            // "Couldn't pull enough data" or "peopleSearch returned no
            // match". One consistent bubble, support escalation path.
            // Also terminate any still-running step (e.g. "Processing your
            // request") so its spinner stops when the error bubble shows.
            markAllRunningAsFailed("")
            setMessages(sessionId, {
              id: assistantMessageId,
              role: "assistant",
              isPending: false,
              askUser: null,
              _isPmbV2: true,
              error: GENERIC_ERROR_MESSAGE
            })
            return
          }

          const existingContent: any = (cur as any)?.content || {}
          const existingCards: any[] = Array.isArray(existingContent.cards)
            ? existingContent.cards
            : []
          const newCard = {
            stepId: data?.step_id || "",
            resourceName: data?.resource_name,
            // Per-turn remount token for the v2 panel keys (ENG-2502/2503).
            // Message id, not step_id — step_id is `s1` on every turn.
            resourceParams: {
              ...data?.params,
              _iterationToken: `${assistantMessageId}:${data?.step_id || ""}`
            }
          }
          // Dedupe by step_id — protects against double-emit on
          // resume/replay.
          const seen = new Set(
            existingCards.map((c) => c.stepId || c.resourceName)
          )
          const cards = seen.has(newCard.stepId || newCard.resourceName)
            ? existingCards
            : [...existingCards, newCard]
          // DIAG: log the merge transition so we can see whether the
          // second ui_component event actually saw the first one's
          // committed state. Pairs with the FE frame-parse log above
          // + api-getorbitx `forward.frame` log.
          try {
            // eslint-disable-next-line no-console
            console.debug(
              `[v2-sse] ui_component merge step=${newCard.stepId} resource=${newCard.resourceName} ` +
                `existing=${existingCards.length} after=${cards.length} ` +
                `existingSteps=[${existingCards.map((c) => c.stepId).join(",")}]`
            )
          } catch (err) {
            // best-effort ui_component merge debug log.
            // eslint-disable-next-line no-console
            console.error("[v2-debug] ui_component merge log failed", err)
          }
          const firstCard = cards[0] || newCard
          const cardContent = {
            // RichTextEditor renders content.parts[0] above the card.
            // Synth's text lands here BEFORE ui_component events arrive
            // under the new executor ordering — preserve it.
            parts: existingContent.parts || [""],
            // First card is the legacy top-level pointer for renderers
            // that haven't been updated to read `cards`.
            resourceName: firstCard.resourceName,
            resourceParams: firstCard.resourceParams,
            // Full ordered list — renderers should iterate this.
            cards,
            // Match the message-list key for the first card so the auto-opened
            // preview highlights its card (ENG-1905 highlight edge case).
            _previewKey: getCardPreviewKey(assistantMessageId, firstCard, 0)
          }
          setMessages(sessionId, {
            id: assistantMessageId,
            role: "assistant",
            content_type: "ui_component",
            content: cardContent,
            isPending: false,
            askUser: null
          })
          // Auto-open the right panel for the FIRST card only — mirrors
          // V1's agent-search.tsx behavior. Subsequent cards stack in
          // the bubble; user clicks each to open its drawer.
          if (cards.length === 1) {
            setShowSplitter(cardContent)
            if (cardContent.resourceName === "SalesPitchDeckBuilderV2") {
              pitchDeckV2SplitterSetRef.current = true
            }
          }
          return
        }

        case "sources_collected": {
          // Backend's _LeafFinalizer emits this AFTER synth_completed
          // with the union of websearch / SerpAPI URLs collected by
          // leaf-step tool calls. The Sources drawer reads its data
          // from the `done` frame's web_search_results (see below); this
          // event drives the live "Found N sources" chip in the step
          // timeline AND is stashed as a fallback for the Sources drawer:
          // the planner/executor path emits sources here but doesn't
          // always echo them onto the done frame, so without this the
          // chips go missing despite "Collected N sources" showing.
          if (Array.isArray(data?.sources) && data.sources.length > 0) {
            leafWebSourcesRef.current = data.sources
          }
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_sources_collected", {
              count: data?.count ?? (data?.sources?.length || 0),
              defaultValue: `Collected ${data?.count ?? 0} sources`
            }),
            status: "done",
            raw: data
          })
          return
        }

        case "done": {
          const final = data?.final_answer || ""
          // Awaiting-user terminal: py emits `done` with
          // status="awaiting_user" when the worker is parked on
          // ask_user (planner clarification, in-plan step_confirmation,
          // execution failure surface, or synth-critic block). This
          // is NOT a completion — the SSE stream closes cleanly but
          // the form below the chip must stay visible so the user can
          // answer. Treat it as "stop the spinner, do not touch
          // askUser, content, sources, or suggestions". The next user
          // action (form submit) opens a fresh resume stream that
          // emits its own done with status="done" or "failed".
          if (data?.status === "awaiting_user") {
            // Clear any stale error painted by a prior tail-check that
            // fired before the BE's awaiting_user done frame arrived.
            // The bubble should reflect the new awaiting state, not the
            // ghost error from the closed-without-done pause.
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              error: null
            })
            return
          }
          // Failure path — py emits `done` with status="failed" and a
          // technical `reason` (e.g., "litellm.InternalServerError:
          // AnthropicError - ... overloaded_error ..."). Surface the
          // canonical error bubble; never leak the technical reason
          // into the user-facing UI. Common transient cause is
          // Anthropic's grammar-compiler overload — the BE retries up
          // to 3× with backoff before emitting this; user just needs
          // to try again in a few seconds.
          if (data?.status === "failed") {
            // ENG-1167/1293: surface the technical reason that the
            // canonical bubble swallows. This is the most common path
            // for "trouble serving your request" — py emits a `done`
            // frame with status=failed and a `reason` string (e.g.
            // litellm InternalServerError, Anthropic overload, planner
            // exception). The bubble shows the friendly text; the
            // console gets the actual cause.
            // eslint-disable-next-line no-console
            console.error("[v2-debug] done frame with status=failed", {
              reason: data?.reason,
              error: data?.error,
              invocation_id: data?.invocation_id,
              data
            })
            // const reasonStr = String(data?.reason || "")
            // const looksTransient =
            //   /overloaded|temporarily unavailable|grammar compilation|503|502|timeout/i.test(
            //     reasonStr
            //   )
            // Stop the in-flight spinners. Without this, any running
            // plan_step_started / plan_step_substep rows spin forever
            // because the BE died mid-stream (e.g. NameError in _after
            // → ParallelAgent aclose RuntimeError → done frame fires
            // with status=failed but no plan_step_completed for the
            // active step). The planner_error / executor_error cases
            // already do this; the generic `done` failure path didn't.
            markAllRunningAsFailed(data?.reason || data?.error || "")
            const errorMsg = GENERIC_ERROR_MESSAGE
            setMessages(sessionId, {
              id: assistantMessageId,
              role: "assistant",
              isPending: false,
              askUser: null,
              error: errorMsg,
              msg_id: data?.invocation_id || assistantMessageId
            })
            return
          }
          // Safety net: any chip still in `running` when the done frame
          // arrives gets flipped to done. Happens when a sub-agent's
          // plan_step_completed went missing or arrived after the
          // synth fired without resetting the step row's spinner.
          // Without this sweep, the user sees the final answer but a
          // residual spinner sits in the timeline forever. We mark
          // every event_type, not just plan_step_started — same intent
          // as markAllRunningStepsDoneOnSynthStart, broader scope
          // because done is the final terminator.
          {
            const _cur =
              useOsAgentsIdStore.getState().messages?.[sessionId]?.[
                assistantMessageId
              ] || {}
            const _arr = [...(((_cur as any).agentSteps as any[]) || [])]
            let _mutated = false
            for (let i = 0; i < _arr.length; i++) {
              if (_arr[i].status === "running") {
                _arr[i] = { ..._arr[i], status: "done" }
                _mutated = true
              }
            }
            if (_mutated) {
              setMessages(sessionId, {
                id: assistantMessageId,
                agentSteps: _arr
              })
            }
          }
          // Show BOTH cards AND the synth text from the done frame
          // (ENG-1296). The renderer (agent-id-messages-v2.tsx#L416 +
          // L464) renders the text bubble from content.parts[0] ABOVE
          // the card stack — when both are populated, both render.
          //
          // Two ordering scenarios handled by ONE defensive write:
          //   (a) cards arrived BEFORE done (normal multi-step plan):
          //       content_type = "ui_component", content has cards.
          //       We merge parts=[final] into the existing content;
          //       content_type stays "ui_component".
          //   (b) cards arrived AFTER done (out-of-order replay / retry,
          //       OR a plan that emitted no cards at all):
          //       cur.content may be empty / text-shaped. We seed
          //       content_type="text" only when no cards exist yet, so
          //       a later cards_ready can promote it to "ui_component".
          //
          // Hard rules:
          //   - Always preserve existing content (cards, resourceName,
          //     resourceParams) via spread. Never clobber.
          //   - Always write parts=[final] when final is non-empty.
          //   - Do NOT downgrade content_type from ui_component to text.
          //     Prior code had a !isUiComponentTurn branch that wrote
          //     `content: { content_type: "text", parts: [final] }`
          //     wholesale, erasing cards added in any future tick.
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}
          const existingContent: any = (cur as any)?.content || {}
          const existingContentType = (cur as any)?.content_type
          const hasCards =
            Array.isArray(existingContent.cards) &&
            existingContent.cards.length > 0
          const isUiComponentTurn =
            existingContentType === "ui_component" || hasCards
          const nextContentType: string = isUiComponentTurn
            ? "ui_component"
            : "text"
          setMessages(sessionId, {
            id: assistantMessageId,
            role: "assistant",
            content: {
              ...existingContent,
              parts: [final]
            },
            content_type: nextContentType,
            isPending: false,
            askUser: null,
            // Heal any stale error painted by a tail-check that fired
            // before this done frame arrived (BE pauses for picker /
            // plan-approval close the SSE without done; the resume's
            // done lands here and should clear the ghost bubble).
            error: null,
            msg_id: data?.invocation_id || assistantMessageId
          })
          // Sources ride the `done` frame from py — IDs only for
          // internal docs (no enrichment yet) plus the full
          // web_search_results array. agent-id-messages-v2 detects
          // the {mlRecommendedSearchDocs, web_search_results} object
          // shape and renders the External-sources dropdown when
          // web_search_results is non-empty. Internal-source name
          // enrichment (full doc objects) is a follow-up — for now
          // the Sources button hides if there are no enriched docs.
          // Strict numeric filter: tenant_shared_kb_chunks.id is a
          // bigint column; an empty string or non-digit value reaches
          // the BE and triggers `invalid input syntax for type bigint:
          // ""` (observed 2026-05-12 — a 500 on /sources/resolve).
          // The resolve fetch is wrapped in try/catch so a 500 doesn't
          // crash the UI, but filtering here keeps the BE clean and
          // avoids the unnecessary round-trip + error log.
          const sourceDocIds = Array.isArray(data?.source_doc_ids)
            ? data.source_doc_ids
                .map((id: unknown) => (id == null ? "" : String(id).trim()))
                .filter((id: string) => /^\d+$/.test(id))
            : []
          let webSearchResults = Array.isArray(data?.web_search_results)
            ? data.web_search_results
            : data?.web_search_results?.sources || []
          // Fallback: planner/executor turns emit their web sources on the
          // sources_collected event but may leave the done frame's
          // web_search_results empty. Use the captured leaf sources so the
          // External-sources chip still renders (resolve normalizes them
          // to {link,title} like any other web result). Only kicks in when
          // the done frame brought none — so Ask OrbitShift and other
          // flows that already populate the done frame are unaffected.
          if (
            webSearchResults.length === 0 &&
            leafWebSourcesRef.current.length > 0
          ) {
            webSearchResults = leafWebSourcesRef.current
          }
          const sourceObj =
            sourceDocIds.length > 0 || webSearchResults.length > 0
              ? {
                  // Placeholder shape: each id becomes {id, name: id}.
                  // Final render today filters out by name; future
                  // enrichment will replace these with full doc rows.
                  mlRecommendedSearchDocs: [] as any[],
                  web_search_results: webSearchResults
                }
              : []
          // Seed sources + score + suggestions placeholders on EVERY
          // done frame (was previously gated on !isUiComponentTurn,
          // which left ui_component bubbles without a score/suggestions
          // slot the downstream renderer expects). Content + parts +
          // content_type were already written above — don't touch them
          // here.
          setMessages(sessionId, {
            id: assistantMessageId,
            source: sourceObj,
            score: { score_category: "" },
            suggestions: []
          })
          // Sources for V2 ride the done frame as raw IDs. Enrich
          // them via the new V2 sources/resolve endpoint (which
          // reuses V1's parseQnaMLSource), so the message ends up
          // with the same {mlRecommendedSearchDocs, web_search_results}
          // shape the V1 RFP UI uses. Suggestions fire independently.
          void (async () => {
            if (sourceDocIds.length > 0 || webSearchResults.length > 0) {
              try {
                const v2Base = `${URLS.V1.replace("/v1", "/v2")}`
                const resolveRes = await fetch(
                  `${v2Base}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/sources/resolve`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${tokenInfo?.token}`
                    },
                    body: JSON.stringify({
                      source_ids: sourceDocIds,
                      web_search_results: webSearchResults
                    })
                  }
                )
                if (resolveRes.ok) {
                  const json = await resolveRes.json()
                  if (json?.success && json?.data) {
                    setMessages(sessionId, {
                      id: assistantMessageId,
                      source: {
                        mlRecommendedSearchDocs:
                          json.data.mlRecommendedSearchDocs || [],
                        web_search_results: json.data.web_search_results
                      }
                    })
                  }
                }
              } catch (err) {
                // sources enrichment is best-effort; the raw IDs from
                // the done frame remain on the message so the
                // External-sources dropdown still renders.
                // eslint-disable-next-line no-console
                console.error("[v2-debug] sources enrichment failed", err)
              }
            }
            // Suggestions — accountOS or rfpos surface (KOS purged in PR #2419).
            // v2 BE keys turn lookups + persistence on invocation_id. py
            // omits it from the `done` frame (stream is per-invocation —
            // the URL already pins it). Recover from the message store,
            // which was seeded by the `accepted` start frame emitted by
            // api-getorbitx BEFORE py opens the stream — so it's always
            // populated by the time we reach the `done` handler.
            const storedMsg = useOsAgentsIdStore.getState().messages?.[
              sessionId
            ]?.[assistantMessageId] as
              | {
                  invocationId?: string
                  content?: { cards?: any[]; resourceName?: string }
                }
              | undefined
            const storedInvocationId = storedMsg?.invocationId
            const invocationId = data?.invocation_id || storedInvocationId
            // PptBuilderV2 deck rendering is async beyond chat-side `done`:
            // the Opus pipeline streams via a separate downstream call, so
            // the deck typically lands ~30-60s after the chat stream
            // closes. Firing suggestions on `done` would surface chips
            // next to a still-loading deck. Stash the fetch params in
            // pendingPptSuggestions; AgentPptOpusPanel flushes them once
            // the deck is on screen. Non-PPT turns fetch immediately.
            const turnCards: any[] = storedMsg?.content?.cards || []
            const hasPptBuilderV2Card =
              turnCards.some((c: any) => c?.resourceName === "PptBuilderV2") ||
              storedMsg?.content?.resourceName === "PptBuilderV2"
            if (invocationId) {
              const surface = (() => {
                const fromUrl =
                  sessionStorage.getItem("ask_orbitshift_from_url") || ""
                if (fromUrl.includes("rfp-os")) return "rfpos"
                return "accountOS"
              })()
              if (hasPptBuilderV2Card) {
                useOsAgentsIdStore
                  .getState()
                  .setPendingPptSuggestion(sessionId, {
                    invocationId,
                    assistantMessageId: Number(assistantMessageId),
                    surface,
                    accountId: accountId || "0"
                  })
              } else {
                try {
                  const suggestionsResponse = await getChatSuggestions(
                    tokenInfo!,
                    sessionId,
                    accountId || "0",
                    surface,
                    undefined,
                    invocationId
                  )
                  setMessages(sessionId, {
                    id: assistantMessageId,
                    suggestions:
                      suggestionsResponse.data?.data?.suggestions || []
                  })
                } catch (err) {
                  // suggestions are best-effort.
                  // eslint-disable-next-line no-console
                  console.error("[v2-debug] suggestions fetch failed", err)
                }
              }
            }
          })()
          return
        }

        case "planner_error":
        case "executor_error":
          // ENG-1167/1293: surface the reason that the canonical
          // bubble swallows.
          // eslint-disable-next-line no-console
          console.error(`[v2-debug] ${eventType} frame received`, {
            reason: data?.reason,
            error: data?.error,
            data
          })
          // Backend emitted an explicit failure frame — distinct from
          // a stream-level `error` in that the pipeline ran but the
          // planner / executor couldn't produce a usable result (e.g.
          // empty plan with no steps, plan validation cycle, executor
          // had no plan to execute). Surface as the canonical bubble
          // and stop the spinner. agentSteps history is preserved so
          // the user can scroll back and see what happened.
          markAllRunningAsFailed(data?.reason || data?.error || "")
          appendStep({
            id: ord,
            eventType,
            title:
              eventType === "planner_error"
                ? t("os_agents.label_planner_failed")
                : t("os_agents.label_executor_failed"),
            status: "failed",
            detail: data?.reason || data?.error || "",
            raw: data
          })
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            askUser: null,
            error: GENERIC_ERROR_MESSAGE
          })
          return

        case "error": {
          // Distinguish two categories of error:
          //   (a) Stream paused while askUser was active and the
          //       upstream connection idled out (e.g., ALB closed the
          //       socket after 20 min of no SSE bytes). The pipeline
          //       didn't fail — the user just hadn't answered yet. Keep
          //       the askUser form intact; answering it opens a fresh
          //       resume stream. Don't paint the red error bubble (it
          //       would be misleading + would hide the form).
          //   (b) Real pipeline failure with no recovery affordance —
          //       no askUser to interact with. Show the canonical
          //       error bubble.
          const cur =
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] || {}
          const hasAskUser = Boolean((cur as any).askUser)
          const errMsg = String(data?.error || "")
          const isAbortError = /abort/i.test(errMsg)
          // ENG-1167/1293: surface the raw error string the bubble hides.
          // eslint-disable-next-line no-console
          console.error("[v2-debug] error frame received", {
            errMsg,
            hasAskUser,
            isAbortError,
            data
          })
          // Stop spinners on every still-running step. Skip when this
          // is a "stream paused while askUser was open" abort — the
          // pipeline didn't fail; the steps were genuinely paused, and
          // flipping them to failed would mis-represent what happened.
          if (!(hasAskUser && isAbortError)) {
            markAllRunningAsFailed(errMsg)
          }
          // Surface a step chip ONLY for the user-friendly "connection
          // paused while askUser was open" case — that breadcrumb tells
          // the user why the spinner stopped and that answering will
          // reconnect. For genuine pipeline failures we deliberately
          // skip the chip: titles like "Pipeline error" + raw details
          // like "v2 chat-agent enqueue failed" are tech-jargon noise on
          // top of the friendly GENERIC_ERROR_MESSAGE bubble below.
          if (hasAskUser && isAbortError) {
            appendStep({
              id: ord,
              eventType,
              title: t("os_agents.label_connection_paused_idle"),
              status: "failed",
              detail: errMsg,
              raw: data
            })
          }
          if (hasAskUser) {
            // Soft stop — keep askUser, agentSteps, content intact.
            // Just flip pending off so spinners stop. The askUser
            // card stays visible and submitting it will reconnect.
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false
            })
          } else {
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              askUser: null,
              error: GENERIC_ERROR_MESSAGE
            })
          }
          return
        }

        case "plan_step_paused_for_user":
          // Internal book-keeping frame paired with the `ask_user` frame
          // that follows immediately. The ask_user case renders the
          // user-facing question + input controls; this one's only job
          // here is to terminate the spinner on the matching
          // `plan_step_started` row (no `plan_step_completed` is ever
          // emitted for a paused step). Without this flip the dot
          // animation lives on forever next to the step title. We pass
          // no `detail` — the row's title is already the human-readable
          // step name, and the ask_user card directly below shows the
          // actual question.
          markLastRunningByPrefix("plan_step_started")
          return

        case "plan_step_fallback_triggered":
          // Friendly status chip when the primary agent returned empty
          // data and the runtime is trying the web-search backup.
          // Without an explicit case, this event would fall through to
          // the default that dumps raw JSON to the user ("{title:...,
          // primary_reason:..., step_id:...}"). The follow-up
          // plan_step_completed event will mark the step done; this
          // chip just tells the user that a backup approach is being
          // tried so the brief pause isn't mysterious.
          appendStep({
            id: ord,
            eventType,
            title: t("os_agents.label_trying_backup_lookup"),
            status: "running",
            // No raw detail — the title alone tells the user what's
            // happening. Anything more is internal plumbing.
            raw: data
          })
          return

        default:
          // Any unknown future event type: log for diagnostics but do
          // NOT surface to the user. Previously this branch dumped
          // `JSON.stringify(data).slice(0, 200)` as a chip detail,
          // which leaked internal plumbing (e.g. synth_skipped's
          // `{reason, agent}`) onto the user's timeline. Every
          // known event has an explicit case above; if a new one
          // lands here, add a handler rather than letting JSON leak.
          // eslint-disable-next-line no-console
          console.warn("[agent-search-v2] unhandled SSE event", eventType, data)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  /**
   * User clicked an option on an ask_user / plan_approval prompt. POSTs
   * the decision to the v2 resume endpoint and streams the follow-up
   * invocation's frames into the SAME assistant message row (the same
   * `assistantMessageId` that showed the prompt). This keeps the
   * "conversation turn" semantically contiguous — the user's click isn't
   * a new turn, it's a continuation.
   */
  const resumeWithDecision = useCallback(
    async (
      sessionId: string,
      assistantMessageId: string,
      decision: {
        action: string
        answer?: string | Record<string, string | string[]>
      },
      accountId?: string
    ) => {
      if (!tokenInfo) return
      // Stale-flag recovery: `resumeInFlight` is set to true at the
      // top of every resume/retry/reattach flow and reset in their
      // `finally` blocks. If a prior flow stalled (long network hang,
      // a swallowed promise rejection, a JS error after the `await
      // fetch(...)` but before reaching `finally`), the flag stays
      // true forever — the user clicks Approve and the button goes
      // silently disabled until they reload the page. Force-recover
      // here instead of bailing silently.
      if (resumeInFlight) {
        // eslint-disable-next-line no-console
        console.warn(
          "[v2-resume] stale resumeInFlight detected — force-resetting and aborting stale stream"
        )
        if (abortController) {
          intentionalAbort = true
          try {
            abortController.abort()
          } catch (err) {
            // aborting an already-aborted controller is a no-op.
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] resume: abort threw on stale controller",
              err
            )
          }
        }
        resumeInFlight = false
      }
      resumeInFlight = true
      if (abortController) {
        // Signal the sendMessage catch branch that this abort is
        // intentional so it does not paint the message bubble as errored.
        intentionalAbort = true
        abortController.abort()
      }
      // PMB v2 picker — detect by shape (mode=default OR any pmb_* key
      // present) and render a friendly status line instead of dumping the
      // raw pmb_q1 / pmb_q3 key-value pairs into the chat. Q2 was dropped
      // from the picker on 2026-05-19; match on the pmb_ namespace so this
      // survives further question reshuffles.
      const answerObj =
        decision.answer && typeof decision.answer === "object"
          ? (decision.answer as Record<string, unknown>)
          : null
      const isPmbV2DefaultPick = answerObj?.mode === "default"
      const isPmbV2PersonalizedPick =
        answerObj != null &&
        Object.keys(answerObj).some((k) => k.startsWith("pmb_"))
      const isPmbV2Pick = isPmbV2DefaultPick || isPmbV2PersonalizedPick

      // Render the user's decision as a visible step. For a 1-clarification
      // answer `decision.answer` is a string; for multi-clarification it's
      // a Record, which we stringify compactly. PMB v2 picks bypass that
      // dump and use a clean status line. For everything else, humanize
      // the planner's internal clarification keys (e.g. `account_scope`)
      // so the row reads "Account Scope: …" instead of leaking the raw
      // key — see ENG-1257 and the matching path in v2-history-replay.
      // Look up the prior askUser's clarifications so we can label
      // each answer with its actual question text ("Presales' Person
      // Name: Jasveer") instead of the raw qid_4 key ("Qid 4:
      // Jasveer"). The askUser was on the message just before this
      // call cleared it to null.
      const priorAskUser = (
        useOsAgentsIdStore.getState().messages?.[sessionId]?.[
          assistantMessageId
        ] as any
      )?.askUser
      const priorClarifications = Array.isArray(priorAskUser?.clarifications)
        ? priorAskUser.clarifications
        : []
      const answerSummary =
        typeof decision.answer === "string"
          ? decision.answer
          : decision.answer && !isPmbV2Pick
            ? Object.entries(decision.answer)
                .map(
                  ([k, v]) =>
                    `${humanizeClarificationKey(k, priorClarifications)}: ${Array.isArray(v) ? v.join(", ") : v}`
                )
                .join(" · ")
            : ""

      // decision.action is the internal snake_case enum
      // (`answer_question`, `approve`, `modify`, `cancel`, etc.). Map
      // to a friendly English label via the locale-backed helper —
      // otherwise the user sees "You chose: answer_question → ..."
      // which leaks BE jargon. Same helper used by v2-history-replay
      // so live and historical renders stay in sync.
      const friendlyAction = friendlyDecisionActionLabel(decision.action)
      // Clarification answers (`answer_question`) render as the "Your answers"
      // recap CARD, appended to the timeline optimistically the instant the
      // user submits — fast and in place, right where they answered (before
      // the planner's in-between steps). The FE already knows the questions,
      // answers and which were skipped (decision.answers_recap), so we don't
      // wait on the backend and the backend persists NO separate recap event.
      // On reload the card is rebuilt from the persisted decision turn + the
      // prior ask_user's clarifications (see v2-history-replay.ts). The old
      // inline "You chose: Answer → …" user_decision step is kept only for
      // approve / modify / cancel, which have no recap card.
      const isClarificationAnswer = decision.action === "answer_question"
      const recapAnswers = Array.isArray((decision as any).answers_recap)
        ? ((decision as any).answers_recap as {
            question: string
            answer: string
          }[])
        : []
      const decisionStep = isClarificationAnswer
        ? {
            id: Date.now(),
            eventType: "your_answers",
            title: t("os_agents.label_your_answers"),
            status: "done",
            raw: { answers: recapAnswers }
          }
        : {
            id: Date.now(),
            eventType: "user_decision",
            title: answerSummary
              ? t("os_agents.label_user_chose_action_with_summary", {
                  action: friendlyAction,
                  summary: answerSummary
                })
              : t("os_agents.label_user_chose_action", {
                  action: friendlyAction
                }),
            status: "done"
          }
      const cur =
        useOsAgentsIdStore.getState().messages?.[sessionId]?.[
          assistantMessageId
        ] || {}
      setMessages(sessionId, {
        id: assistantMessageId,
        isPending: true,
        askUser: null,
        error: undefined,
        agentSteps: isPmbV2Pick
          ? ((cur as any).agentSteps as any[]) || []
          : [...(((cur as any).agentSteps as any[]) || []), decisionStep]
      })
      const myAbortController = new AbortController()
      try {
        abortController = myAbortController
        const response = await fetch(
          withLinkedTenant(
            `${URLS.V1.replace("/v1", "/v2")}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${sessionId}/resume`
          ),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo?.token}`
            },
            body: JSON.stringify({
              plan_decision: decision,
              // Echo the stable RFP mode bindings on every resume so
              // py's seed.refresh self-heals if create-turn state ever
              // missed them. Non-destructive — py only updates state
              // for keys present in payload. We don't send
              // research_enabled here: that's a per-turn user toggle
              // and may be stale from a different chat-input
              // instance; create-turn state is authoritative.
              ...(mode === "rfp" &&
                resolvedRfpContext && {
                  is_rfp_os: true,
                  rfp_additional_context: resolvedRfpContext
                })
            }),
            signal: abortController?.signal
          }
        )
        if (!response.ok) {
          // 409 = race guard: the session was advanced elsewhere (another
          // tab approved already, or the previous resume is still
          // streaming). DO NOT destroy the askUser form — the user's
          // decision is salvageable; they just need to retry the
          // submit. Preserve `askUser` so the form is still rendered,
          // and stash the rejected decision so a follow-up Retry click
          // can re-fire the same resume payload (the BE's stale-stub
          // recovery now accepts it on the second try). RCA inv
          // 00f821b0 (2026-05-17): user clicked Answer → 409 →
          // form-destroyed-and-replaced-with-error → user clicked
          // Retry → retry_execution ran with empty plan → answer lost.
          if (response.status === 409) {
            const cur =
              useOsAgentsIdStore.getState().messages?.[sessionId]?.[
                assistantMessageId
              ] || {}
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              // Preserve askUser so the form re-renders.
              askUser: (cur as any).askUser,
              // Store the attempted decision so a Retry click can
              // re-fire as resume instead of retry_execution.
              pendingResumeDecision: decision,
              // Show the stale-session warning text but don't blow
              // away the message bubble.
              error: STALE_SESSION_MESSAGE
            })
            return
          }
          let serverMsg = ""
          try {
            serverMsg = await response.text()
          } catch (err) {
            // body read can fail for aborted responses.
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] resume: failed to read error body",
              response.status,
              err
            )
          }
          throw new Error(
            `Resume failed (${response.status})${serverMsg ? ": " + serverMsg.slice(0, 240) : ""}`
          )
        }
        if (!response.body) {
          throw new Error(t("os_agents.label_readable_stream_unsupported"))
        }
        readerRef.current = response.body.getReader()
        const decoder = new TextDecoder("utf-8")
        let buf = ""
        try {
          // eslint-disable-next-line no-constant-binary-expression
          while (true && isMountedRef.current) {
            const { done, value } = await readerRef.current.read()
            if (done) {
              // eslint-disable-next-line no-console
              console.warn(
                `[v2-sse-resume] reader.read() returned done=true — natural stream end`
              )
              break
            }
            buf += decoder.decode(value, { stream: true })
            let idx: number
            while ((idx = buf.indexOf("\n\n")) !== -1) {
              const frame = buf.slice(0, idx)
              buf = buf.slice(idx + 2)
              if (frame.trim()) {
                // DIAG: log each parsed SSE frame from the RESUME stream
                // — same shape as the chatbotV2 reader's [v2-sse] log.
                try {
                  const ev = frame
                    .split("\n")
                    .find((l) => l.startsWith("event:"))
                  const id = frame.split("\n").find((l) => l.startsWith("id:"))
                  // eslint-disable-next-line no-console
                  console.debug(
                    `[v2-sse-resume] frame parsed ${id || "(no-id)"} ${ev || "(no-event)"}`
                  )
                } catch (err) {
                  // best-effort resume-frame metadata parse.
                  // eslint-disable-next-line no-console
                  console.error(
                    "[v2-debug] resume-frame metadata parse failed",
                    err
                  )
                }
                processV2Frame(sessionId, frame, assistantMessageId, accountId)
                // ENG-1325 follow-up: also stamp lastEventId on resume
                // frames — otherwise a later reattach will dedupe only
                // against sendMessage's highest event_id and re-process
                // resume frames (the "Applying your input" duplicate
                // observed post-resume → switch-away → switch-back).
                const resumeFrameEventId = _parseSseEventId(frame)
                if (resumeFrameEventId !== null) {
                  setMessages(sessionId, {
                    id: assistantMessageId,
                    lastEventId: resumeFrameEventId
                  })
                }
              }
            }
            if (!isMountedRef.current) {
              // eslint-disable-next-line no-console
              console.warn(
                `[v2-sse-resume] isMountedRef.current became false — loop will exit`
              )
            }
          }
          if (buf.trim()) {
            processV2Frame(sessionId, buf, assistantMessageId, accountId)
            const resumeTailEventId = _parseSseEventId(buf)
            if (resumeTailEventId !== null) {
              setMessages(sessionId, {
                id: assistantMessageId,
                lastEventId: resumeTailEventId
              })
            }
          }
        } catch (err) {
          // stream cancelled / network blip — error frames are emitted
          // inside processV2Frame when the backend reports one. Surface
          // for ENG-1167/1293 visibility into "trouble serving" cases
          // that previously didn't show in n/w or console.
          // eslint-disable-next-line no-console
          console.error("[v2-debug] stream read interrupted (resume)", err)
        }
        // Same tail-check as sendMessage. Gate on !intentionalAbort —
        // if a fresh sendMessage mid-resume-stream has aborted us,
        // that NEW handler owns the message row now; don't pre-paint
        // an error it will immediately overwrite. ENG-1325: also gate
        // on deactivatedSessions — if the user navigated away from
        // this session, leave the turn pending so reattach can pick
        // it up on return.
        const tailState =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ]
        if (
          tailState &&
          (tailState as any).isPending &&
          !(tailState as any).askUser &&
          !intentionalAbort &&
          !deactivatedSessions.has(sessionId)
        ) {
          // ENG-1167/1293: stream ended without a terminal frame and
          // the message is still pending — surface for visibility.
          // askUser gate: see sendMessage tail-check rationale.
          // eslint-disable-next-line no-console
          console.error(
            "[v2-debug] stream ended without done frame — painting canonical error",
            {
              sessionId,
              assistantMessageId,
              tailStateKeys: Object.keys(tailState || {})
            }
          )
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } catch (err: any) {
        // AbortError here means the user clicked Submit twice in quick
        // succession, or started a fresh send while the resume was still
        // streaming. That handler owns the message now — don't paint it.
        // Every other failure mode (HTTP, network, fetch-kill) surfaces
        // the same canonical error bubble.
        // DIAG: log EVERY exit (including AbortError) so we can see
        // when the resume's read loop is terminated unexpectedly. We
        // saw a case where 1 ui_component frame parsed → loop exited
        // → second ui_component frame never reached the FE despite
        // api-getorbitx writing it successfully.
        // eslint-disable-next-line no-console
        console.warn(
          `[v2-sse-resume] reader exit name=${err?.name || "(none)"} message=${err?.message || ""} intentionalAbort=${intentionalAbort}`
        )
        if (err?.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error("[v2-resume] fetch/read failed", {
            name: err?.name,
            message: err?.message
          })
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } finally {
        // eslint-disable-next-line no-console
        console.warn(
          `[v2-sse-resume] reader finally — cancelling readerRef.current=${!!readerRef.current}`
        )
        readerRef.current?.cancel()
        readerRef.current = null
        // Do NOT abort the live `abortController` here on success — that
        // would spuriously reject any downstream reader / fetch that's
        // still in progress (it isn't ours to kill on cleanup).
        //
        // Identity-check before nulling out: if another handler has
        // already swapped in a newer controller, leave it alone.
        if (abortController === myAbortController) {
          abortController = null
        }
        resumeInFlight = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenInfo, setMessages, processV2Frame]
  )

  // ─────────────────────────────────────────────────────────────────────
  // TEMP(ENG-2274 demo) — AUTO-APPROVE the account-plan plan gate.
  // The account-plan workbench is meant to direct-route (skip the planner +
  // approval) via the backend `is_account_plan` fast-lane. Until that backend
  // is deployed to this env, the turn falls through to the normal planner and
  // shows a "Approve & run" gate. To match the sales-pitch "straight to output"
  // UX for the demo, auto-fire the approve decision the moment a plan_approval
  // prompt appears (account-plan workbench only). The plan is still built
  // server-side — this only removes the manual click.
  //
  // ⚠️ REMOVE THIS BLOCK once the backend `is_account_plan` direct-route ships
  // (otherwise it silently swallows the approval gate). It is deliberately
  // scoped to isAccountPlan + source === "plan_approval" so it can never
  // auto-answer a real clarification ask_user.
  const autoApprovedPlanIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!isAccountPlan || !existingSessionId || !sessionMessages) return
    for (const msg of Object.values(sessionMessages) as any[]) {
      if (
        msg?.askUser?.source === "plan_approval" &&
        msg?.id &&
        !autoApprovedPlanIdsRef.current.has(msg.id)
      ) {
        autoApprovedPlanIdsRef.current.add(msg.id)
        resumeWithDecision(
          existingSessionId,
          msg.id,
          { action: "approve" },
          (accountIdOverride ?? selectedAccount) || undefined
        )
      }
    }
  }, [
    isAccountPlan,
    existingSessionId,
    sessionMessages,
    resumeWithDecision,
    accountIdOverride,
    selectedAccount
  ])
  // ──────────────────────────── end TEMP block ─────────────────────────

  /**
   * Re-run a previously-failed turn's plan.
   *
   * Used by the inline "Retry" button on the error bubble (typically
   * after we deployed a fix and the user wants to re-run without
   * re-typing the query). Mirrors `resumeWithDecision` shape but hits
   * the `/retry` endpoint and skips the "must be awaiting_user" race
   * guard on the BE — failed turns are exactly the trigger.
   *
   * The BE's approval_gate clears stale failed-step state and force-
   * transfers to PlanExecutorAgent; Gate -1 in step_runner skips
   * already-completed steps so only failed/blocked steps re-run.
   *
   * Reuses the same assistantMessageId so the timeline grows in place
   * — the user sees their original step rows, then a new
   * "user_decision: retry" marker, then the steps that re-run.
   */
  const retryExecution = useCallback(
    async (
      sessionId: string,
      assistantMessageId: string,
      failedInvocationId?: string
    ) => {
      if (!tokenInfo) return
      // Stale-flag recovery — see resumeWithDecision for rationale.
      if (resumeInFlight) {
        // eslint-disable-next-line no-console
        console.warn(
          "[v2-retry] stale resumeInFlight detected — force-resetting and aborting stale stream"
        )
        if (abortController) {
          intentionalAbort = true
          try {
            abortController.abort()
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] retry: abort threw on stale controller",
              err
            )
          }
        }
        resumeInFlight = false
      }
      resumeInFlight = true
      if (abortController) {
        intentionalAbort = true
        abortController.abort()
      }
      const cur =
        useOsAgentsIdStore.getState().messages?.[sessionId]?.[
          assistantMessageId
        ] || {}
      setMessages(sessionId, {
        id: assistantMessageId,
        isPending: true,
        askUser: null,
        error: undefined,
        agentSteps: [
          ...(((cur as any).agentSteps as any[]) || []),
          {
            id: Date.now(),
            eventType: "user_decision",
            title: t("os_agents.label_user_chose_retry"),
            status: "done"
          }
        ]
      })
      const myAbortController = new AbortController()
      try {
        abortController = myAbortController
        const response = await fetch(
          withLinkedTenant(
            `${URLS.V1.replace("/v1", "/v2")}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${sessionId}/retry`
          ),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenInfo?.token}`
            },
            body: JSON.stringify({
              invocation_id: failedInvocationId || null,
              // Same RFP-mode echo as the resume path — keeps state
              // bindings present even after a retry on a session that
              // missed them at create.
              ...(mode === "rfp" &&
                resolvedRfpContext && {
                  is_rfp_os: true,
                  rfp_additional_context: resolvedRfpContext
                })
            }),
            signal: abortController?.signal
          }
        )
        if (!response.ok) {
          if (response.status === 409) {
            // Stale or in-flight — the same UX as /resume's 409. The
            // session has advanced; tell the user to refresh.
            setMessages(sessionId, {
              id: assistantMessageId,
              isPending: false,
              error: STALE_SESSION_MESSAGE
            })
            return
          }
          let serverMsg = ""
          try {
            serverMsg = await response.text()
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              "[v2-debug] retry: failed to read error body",
              response.status,
              err
            )
          }
          throw new Error(
            `Retry failed (${response.status})${serverMsg ? ": " + serverMsg.slice(0, 240) : ""}`
          )
        }
        if (!response.body) {
          throw new Error(t("os_agents.label_readable_stream_unsupported"))
        }
        readerRef.current = response.body.getReader()
        const decoder = new TextDecoder("utf-8")
        let buf = ""
        try {
          // eslint-disable-next-line no-constant-binary-expression
          while (true && isMountedRef.current) {
            const { done, value } = await readerRef.current.read()
            if (done) break
            buf += decoder.decode(value, { stream: true })
            let idx: number
            while ((idx = buf.indexOf("\n\n")) !== -1) {
              const frame = buf.slice(0, idx)
              buf = buf.slice(idx + 2)
              if (frame.trim()) {
                processV2Frame(sessionId, frame, assistantMessageId, "")
                // ENG-1325 follow-up: stamp lastEventId on retry frames
                // for the same reason as sendMessage / resume — a
                // reattach must dedupe against the highest event_id
                // we've ever processed on this bubble, regardless of
                // which stream delivered it.
                const retryFrameEventId = _parseSseEventId(frame)
                if (retryFrameEventId !== null) {
                  setMessages(sessionId, {
                    id: assistantMessageId,
                    lastEventId: retryFrameEventId
                  })
                }
              }
            }
          }
          if (buf.trim()) {
            processV2Frame(sessionId, buf, assistantMessageId, "")
            const retryTailEventId = _parseSseEventId(buf)
            if (retryTailEventId !== null) {
              setMessages(sessionId, {
                id: assistantMessageId,
                lastEventId: retryTailEventId
              })
            }
          }
        } catch (err) {
          // error frames surface inside processV2Frame — but log the
          // outer stream interruption for ENG-1167/1293 visibility.
          // eslint-disable-next-line no-console
          console.error("[v2-debug] stream read interrupted (retry)", err)
        }
        const tailState =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ]
        // ENG-1325: also gate on deactivatedSessions so a navigate-
        // away mid-retry doesn't strand the prior session with an
        // error.
        if (
          tailState &&
          (tailState as any).isPending &&
          !(tailState as any).askUser &&
          !intentionalAbort &&
          !deactivatedSessions.has(sessionId)
        ) {
          // ENG-1167/1293: stream ended without a terminal frame and
          // the message is still pending — surface for visibility.
          // askUser gate: see sendMessage tail-check rationale.
          // eslint-disable-next-line no-console
          console.error(
            "[v2-debug] stream ended without done frame — painting canonical error",
            {
              sessionId,
              assistantMessageId,
              tailStateKeys: Object.keys(tailState || {})
            }
          )
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } catch (err: any) {
        // ENG-1167/1293: surface the technical specifics that the
        // canonical bubble swallows.
        // eslint-disable-next-line no-console
        console.error("[v2-debug] retry failed", {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
          err
        })
        if (err?.name !== "AbortError") {
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } finally {
        readerRef.current?.cancel()
        readerRef.current = null
        if (abortController === myAbortController) {
          abortController = null
        }
        resumeInFlight = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenInfo, setMessages, processV2Frame]
  )

  /**
   * Re-attach to an in-flight invocation after a page reload.
   *
   * Triggered by the route loader when v2-history-replay sees the LAST
   * turn with status="in_progress" — the original POST stream was cut
   * by the FE close, but the py worker is still running and its events
   * keep landing in `agent_invocation_event_stream`. This opens the
   * api-getorbitx re-attach endpoint, which proxies py's SSE replay
   * (from event_id=0 by default) into the SAME assistantMessageId the
   * original sendMessage used.
   *
   * History replay deliberately leaves `assistantSteps` empty for
   * in_progress turns (see v2-history-replay._replayV2Turn) so the live
   * frames re-streamed here populate a fresh timeline rather than
   * stacking onto already-painted historical rows. End state: the
   * timeline reflects the live worker view, not a duplicate.
   */
  const reattachToInvocation = useCallback(
    async (
      sessionId: string,
      assistantMessageId: string,
      invocationId: string,
      accountId?: string
    ) => {
      if (!tokenInfo) return
      // eslint-disable-next-line no-console
      console.info(
        `[v2-reattach] opening stream session=${sessionId} invocation=${invocationId}`
      )
      if (abortController) {
        intentionalAbort = true
        abortController.abort()
      }
      const myAbortController = new AbortController()
      try {
        abortController = myAbortController
        const response = await fetch(
          withLinkedTenant(
            `${URLS.V1.replace("/v1", "/v2")}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/chatbot/session/${sessionId}/stream/${invocationId}`
          ),
          {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${tokenInfo?.token}`
            },
            signal: abortController?.signal
          }
        )
        if (!response.ok) {
          // ENG-1167/1293: surface the status the canonical bubble
          // hides. 404 typically = invocation not found / already
          // cleaned up; 5xx = BE wedge.
          // eslint-disable-next-line no-console
          console.error(
            "[v2-debug] reattach non-2xx — painting canonical error",
            {
              status: response.status,
              statusText: response.statusText
            }
          )
          // 404 = invocation not found / already cleaned up. Convert
          // the stuck "in progress" UI to a generic error so the user
          // can retry rather than spin forever.
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
          return
        }
        if (!response.body) return
        readerRef.current = response.body.getReader()
        const decoder = new TextDecoder("utf-8")
        let buf = ""
        // ENG-1325 follow-up: BE replays from event_id=0 on reattach,
        // so any frames whose id is ≤ what we already processed in the
        // ORIGINAL stream are duplicates. Seed the dedupe threshold
        // from the bubble's last-stamped event_id (sendMessage's loop
        // stamps it on every processed frame). Threshold also moves
        // forward as fresh-to-us frames arrive so a reattach-of-a-
        // reattach (rare) is still safe.
        let maxProcessedEventId =
          ((
            useOsAgentsIdStore.getState().messages?.[sessionId]?.[
              assistantMessageId
            ] as any
          )?.lastEventId as number | undefined) ?? -1
        try {
          // eslint-disable-next-line no-constant-binary-expression
          while (true && isMountedRef.current) {
            const { done, value } = await readerRef.current.read()
            if (done) break
            buf += decoder.decode(value, { stream: true })
            let idx: number
            while ((idx = buf.indexOf("\n\n")) !== -1) {
              const frame = buf.slice(0, idx)
              buf = buf.slice(idx + 2)
              if (frame.trim()) {
                const frameEventId = _parseSseEventId(frame)
                if (
                  frameEventId !== null &&
                  frameEventId <= maxProcessedEventId
                ) {
                  // Already processed in the original stream — skip
                  // to avoid duplicating agentSteps in the UI.
                  continue
                }
                processV2Frame(sessionId, frame, assistantMessageId, accountId)
                if (frameEventId !== null) {
                  maxProcessedEventId = frameEventId
                  setMessages(sessionId, {
                    id: assistantMessageId,
                    lastEventId: frameEventId
                  })
                }
              }
            }
          }
          if (buf.trim()) {
            const tailEventId = _parseSseEventId(buf)
            if (tailEventId === null || tailEventId > maxProcessedEventId) {
              processV2Frame(sessionId, buf, assistantMessageId, accountId)
              if (tailEventId !== null) {
                setMessages(sessionId, {
                  id: assistantMessageId,
                  lastEventId: tailEventId
                })
              }
            }
          }
        } catch (err) {
          // stream cancelled / network blip.
          // eslint-disable-next-line no-console
          console.error("[v2-debug] stream read interrupted (reattach)", err)
        }
        const tailState =
          useOsAgentsIdStore.getState().messages?.[sessionId]?.[
            assistantMessageId
          ]
        // ENG-1325: gate on deactivatedSessions so a navigate-away
        // mid-reattach doesn't strand the prior session with an error.
        if (
          tailState &&
          (tailState as any).isPending &&
          !intentionalAbort &&
          !deactivatedSessions.has(sessionId)
        ) {
          // Stream ended without a terminal frame and the message is
          // still pending — same recovery as sendMessage's tail-check.
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } catch (err: any) {
        // ENG-1167/1293: surface the technical specifics that the
        // canonical bubble swallows.
        // eslint-disable-next-line no-console
        console.error("[v2-debug] reattach failed", {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
          err
        })
        if (err?.name !== "AbortError") {
          setMessages(sessionId, {
            id: assistantMessageId,
            isPending: false,
            error: GENERIC_ERROR_MESSAGE
          })
        }
      } finally {
        readerRef.current?.cancel()
        readerRef.current = null
        if (abortController === myAbortController) {
          abortController = null
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokenInfo, setMessages, processV2Frame]
  )

  // Cache the freshly-created session for the lifetime of this component so
  // repeated `ensureSessionId()` calls (one from upload, one from onSubmit)
  // resolve to the same session — otherwise the second call mints a new
  // session and the query lands in a different bucket than the files.
  // The promise ref dedupes parallel calls in the same tick.
  const createdSessionIdRef = useRef<string | null>(null)
  const sessionCreationPromiseRef = useRef<Promise<string> | null>(null)
  // First-query handoff for caller-controlled session create. RFP-OS's
  // create-session endpoint records `question` as the session title, so
  // we MUST pass the user's actual message (not "") on first send.
  // onSubmit writes here just before awaiting ensureSessionId; the
  // refs default to "" so AgentChatInput's pre-send upload path (no
  // user message yet) still works — V1 RFP fresh-session.tsx makes
  // the same trade-off (empty question on upload-before-send).
  const pendingFirstQueryRef = useRef<string>("")
  const pendingResearchEnabledRef = useRef<boolean>(false)
  const ensureSessionId = useCallback(async (): Promise<string> => {
    if (existingSessionId) return existingSessionId
    if (createSession) {
      // Caller-controlled creator (e.g., RFP-OS sub-session create
      // via useCreateAgentSession). Coalesces concurrent calls the
      // same way the default path does.
      if (sessionCreationPromiseRef.current) {
        return sessionCreationPromiseRef.current
      }
      const promise = (async () => {
        const id = await createSession({
          firstQuery: pendingFirstQueryRef.current,
          researchEnabled: pendingResearchEnabledRef.current
        })
        return id
      })()
      sessionCreationPromiseRef.current = promise
      try {
        return await promise
      } finally {
        sessionCreationPromiseRef.current = null
      }
    }
    if (createdSessionIdRef.current) return createdSessionIdRef.current
    if (sessionCreationPromiseRef.current)
      return sessionCreationPromiseRef.current
    const promise = (async () => {
      const newSessionResponse = await createOsAgentsSession(
        tokenInfo as ITokenInfo,
        messageRef.current?.value.trim() || ""
      )
      const id = newSessionResponse?.data?.data?.session_id as string
      if (id) createdSessionIdRef.current = id
      return id
    })()
    sessionCreationPromiseRef.current = promise
    try {
      return await promise
    } finally {
      sessionCreationPromiseRef.current = null
    }
  }, [existingSessionId, tokenInfo])

  useEffect(() => {
    if (!existingSessionId) {
      createdSessionIdRef.current = null
      sessionCreationPromiseRef.current = null
    }
  }, [existingSessionId])

  const onSubmit = useCallback(
    async (submitObj: {
      tool_id?: string
      agentQuery?: string
      accountId?: string
      files?: {
        name: string
        id: string
        source?: string
        original_s3_path?: string
        file_extension?: string
      }[]
      message?: string
      researchEnabled?: boolean
      isTemplateFill?: boolean
      transferToAgent?: string
      pmbBriefId?: string
      pmbContext?: Record<string, unknown> | null
      pmbForceDefault?: boolean
      displayText?: string
      source?: string
      projectName?: string
    }) => {
      const {
        tool_id = "",
        agentQuery = "",
        accountId = "",
        files = [],
        message: messageOverride,
        researchEnabled = false,
        isTemplateFill = false,
        transferToAgent: transferToAgentArg,
        pmbBriefId,
        pmbContext,
        pmbForceDefault,
        displayText,
        source,
        projectName
      } = submitObj || {}
      // Typed follow-ups on a pinned surface carry the surface's agent hint;
      // an explicit per-send transferToAgent (tool CTAs) still wins.
      const transferToAgent = transferToAgentArg ?? defaultTransferToAgent
      const rawMessage =
        messageOverride || agentQuery || messageRef.current?.value.trim() || ""
      const baseTrimmed = rawMessage.trim()
      if (!baseTrimmed && !tool_id) return
      setPendingMessage(tool_id || "input")

      // When an account is pinned, suffix the planner-bound query with
      // the `(internal account id: <id>)` disambiguator so it doesn't
      // emit a "Which account?" clarification when the name is
      // ambiguous (RCA inv 009eed90).
      //
      // displayText (set by suggestion-chip click) is a curated label
      // the LLM already wrote with the account anchor in mind — adding
      // " - <account>" produces a redundant "Prepare brief for 7-Eleven
      // Salesforce breach - 7-Eleven" bubble. So we ONLY append the
      // visible account suffix on free-text turns (no displayText).
      // Chip clicks keep their original label intact.
      const hasAccount = !!(selectedAccountName && selectedAccount)
      const trimmed =
        hasAccount && baseTrimmed
          ? `${baseTrimmed} - ${selectedAccountName} (internal account id: ${selectedAccount})`
          : baseTrimmed
      const userVisibleText = displayText
        ? displayText
        : hasAccount && baseTrimmed
          ? `${baseTrimmed} - ${selectedAccountName}`
          : baseTrimmed

      // Hand the first-send context off to ensureSessionId so RFP-OS's
      // sub-session is created with the actual user query as its
      // title (V1 fresh-session.tsx parity). On subsequent sends the
      // session already exists and these refs are ignored.
      pendingFirstQueryRef.current = trimmed
      pendingResearchEnabledRef.current = !!researchEnabled

      const newSessionId = await ensureSessionId()

      const account = accountId || selectedAccount
      const toolId =
        account === "all_accounts" ? "GenerateAcrossAccountsAgent" : tool_id
      // Post-create redirect is Ask-OS-only — Ask-OS lives on
      // /os/agents/:id and the freshly created session needs the
      // route to switch. RFP-OS stays on /rfp-os/...; the caller's
      // `createSession` already pinned the new sub-session into
      // useRfpOsQnaStore so the surrounding panel re-renders the
      // existing-session view. Navigating here would yank the user
      // out of the RFP-OS workspace entirely.
      //
      // We defer the actual navigate until sendMessage signals the
      // POST has been ACK'd. Otherwise a chunk-error reload (triggered
      // by route transition + new build) tears down the in-flight POST
      // and the session is orphaned with no user turn persisted —
      // user lands on /os/agents/:id with just a stuck spinner.
      // Embedded (drawer) mode never redirects — a fresh session must stay
      // in the panel (no URL change). Instead we surface the new id so the
      // host swaps its embedded message view onto it.
      if (embedded && !existingSessionId && newSessionId) {
        onSessionCreated?.(newSessionId)
      }
      const shouldRedirect = !existingSessionId && mode === "ask" && !embedded
      // When the page mounted us from the proactive-proposal entry route,
      // keep the user inside that namespace instead of yanking them over
      // to /os/agents. Path check is the discriminator — the entry route
      // is the only place that mounts AgentSearchV2 outside /os/agents
      // with mode="ask".
      const onProactiveProposalPath = location.pathname.startsWith(
        "/rfp-os/proactive-proposal"
      )
      const redirectBasePath = onProactiveProposalPath
        ? "/rfp-os/proactive-proposal"
        : "/os/agents"
      const projectQs = onProactiveProposalPath
        ? searchParams.get("project")
        : null
      const redirectQuery = [
        selectedAccount ? `account=${selectedAccount}` : null,
        projectQs ? `project=${encodeURIComponent(projectQs)}` : null
      ]
        .filter(Boolean)
        .join("&")
      const redirectUrl = shouldRedirect
        ? `${redirectBasePath}/${newSessionId}` +
          (redirectQuery ? `?${redirectQuery}` : "")
        : ""

      sendMessage({
        sessionId: newSessionId,
        message: trimmed,
        selectedAccount: (account === "all_accounts" ? "" : account) || "",
        tool_id: toolId,
        files,
        researchEnabled,
        isTemplateFill,
        transferToAgent,
        pmbBriefId,
        pmbContext,
        pmbForceDefault,
        displayText: userVisibleText,
        source,
        projectName,
        onPostCommitted: shouldRedirect
          ? () => navigate(redirectUrl)
          : undefined
      })

      if (messageRef.current) {
        messageRef.current.value = ""
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ensureSessionId,
      existingSessionId,
      navigate,
      selectedAccount,
      sendMessage,
      embedded,
      onSessionCreated,
      defaultTransferToAgent
    ]
  )

  useEffect(() => {
    if (onSubmitRef) {
      // Preserve the callable shape existing callers depend on, but also
      // expose `resume` as a property so the MessageItem render path can
      // trigger a plan_decision follow-up without plumbing a second ref.
      // (agent-id-messages.tsx calls `sendMessageRef.current.resume(...)`.)
      const fn = ((data: any) => onSubmit(data)) as any
      fn.resume = (
        sessionId: string,
        assistantMessageId: string,
        decision: {
          action: string
          answer?: string | Record<string, string | string[]>
        },
        accountId?: string
      ) =>
        resumeWithDecision(sessionId, assistantMessageId, decision, accountId)
      // Inline-retry on a failed message bubble — see retryExecution
      // docstring. Different shape from `resume` (no decision payload,
      // just the failed invocation_id).
      fn.retry = (
        sessionId: string,
        assistantMessageId: string,
        failedInvocationId?: string
      ) => retryExecution(sessionId, assistantMessageId, failedInvocationId)
      // Re-attach SSE on a still-running invocation after a page reload.
      // Used by the route loader path after v2-history-replay sees a
      // last turn with status="in_progress".
      fn.reattach = (
        sessionId: string,
        assistantMessageId: string,
        invocationId: string,
        accountId?: string
      ) =>
        reattachToInvocation(
          sessionId,
          assistantMessageId,
          invocationId,
          accountId
        )
      onSubmitRef.current = fn
    }
  }, [
    onSubmitRef,
    onSubmit,
    resumeWithDecision,
    retryExecution,
    reattachToInvocation
  ])

  useEffect(() => {
    // ENG-1325: arm the reattach loop guard AND clear any prior
    // deactivation marker for this session. React Router reuses the
    // AgentSearch instance across /os/agents/:id param changes (no
    // key on the Outlet), so isMountedRef would otherwise stay false
    // after the first chat switch and reattach's read loop would
    // exit immediately — leaving the bubble stuck on a loader.
    let fallbackReattachTimer: ReturnType<typeof setTimeout> | null = null
    if (existingSessionId) {
      isMountedRef.current = true
      deactivatedSessions.delete(existingSessionId)

      // ENG-1325 fallback reattach: the route-mount hint at
      // os.agents_.$id.tsx:129-165 only fires when the loader's
      // freshly-fetched BE history reports the last turn as exactly
      // `in_progress`. That misses two real cases we hit in this bug:
      //   1. BE worker completed AFTER the FE disconnected, so on
      //      return the turn is `done` (not in_progress) — no hint —
      //      but the in-memory bubble still has isPending=true from
      //      before the disconnect, so the UI sits forever on a
      //      spinner.
      //   2. Loader response cached / not yet refreshed, so v2History
      //      hasn't caught up with reality.
      // The "accepted" frame on the original send already wrote
      // `invocationId` onto the bubble (line ~1067), so the FE has
      // everything it needs to reattach without waiting for the BE
      // history hint. Short defer to let the route-mount hint win
      // first if it's going to (avoids redundant reattaches).
      const sid = existingSessionId
      fallbackReattachTimer = setTimeout(() => {
        if (deactivatedSessions.has(sid)) return
        if (readerRef.current) return // route hint already streaming
        const sessionMessages = useOsAgentsIdStore.getState().messages?.[sid]
        if (!sessionMessages) return
        for (const [msgId, msg] of Object.entries(sessionMessages)) {
          const m = msg as any
          if (m?.isPending && m?.invocationId && !m?.error) {
            // eslint-disable-next-line no-console
            console.info(
              `[v2-reattach] fallback firing for stuck pending bubble session=${sid} invocation=${m.invocationId}`
            )
            reattachToInvocation(
              sid,
              msgId,
              m.invocationId,
              selectedAccount || undefined
            )
            break
          }
        }
      }, 600)
    }
    return () => {
      if (fallbackReattachTimer) {
        clearTimeout(fallbackReattachTimer)
      }
      if (existingSessionId) {
        isMountedRef.current = false
        // ENG-1325: mark this session as deactivated so the dying
        // stream's tail-check skips the canonical-error paint. Removed
        // again when the user returns (effect body above).
        deactivatedSessions.add(existingSessionId)
        setPendingMessage("")
        if (readerRef.current) {
          readerRef.current.cancel().catch(() => {})
          readerRef.current = null
        }
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current)
          debounceTimeout.current = null
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingSessionId])

  // Forward drag-dropped files (deposited into the store by the global
  // drag overlay) to AgentChatInput's upload pipeline.
  useEffect(() => {
    if (droppedFiles.length > 0) {
      chatInputRef.current?.addFiles(droppedFiles)
      clearDroppedFiles()
    }
  }, [droppedFiles, clearDroppedFiles])

  const autoTriggeredRef = useRef(false)
  useEffect(() => {
    // V2 chat only supports the v2 sales-pitch-deck deeplink shape
    // (?action=sales_pitch_v2). The legacy `?action=sales_pitch`
    // (V1's pitch-deck-V1 deeplink) is intentionally unsupported in
    // V2 chat — V2 has a single PPT path, the V2 agent.
    if (
      actionParam === "sales_pitch_v2" &&
      selectedAccount &&
      selectedOpportunity &&
      !autoTriggeredRef.current &&
      !existingSessionId
    ) {
      autoTriggeredRef.current = true
      const query = "Generate a sales pitch deck for this opportunity"
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.value = query
        }
        onSubmit({
          agentQuery: query,
          accountId: selectedAccount,
          // Direct-route hint: bypass planner LLM and force-route to
          // V2 sales-pitch-deck agent. Agent reads `state.opportunity_id`
          // (seeded by worker invocation_runner from the FE payload's
          // `opportunity_id` query param) and calls
          // begin_sales_pitch_deck_v2_tool with it. Saves ~25-30s of
          // planner round-trip on this auto-triggered click flow.
          transferToAgent: "GenerateSalesPitchDeckV2Agent"
        })
      }, 500)
    }
    // PMB v2 entry no longer routes through /os/agents — the
    // <PreMeetingBriefV2Workbench /> portal in app/root.tsx opens an
    // overlay on the current page when a user clicks "Generate
    // Pre-Meeting Brief" anywhere in the app. The workbench itself
    // (containers/pre-meeting-brief-v2) handles session creation,
    // createPmbV2Brief, and the seeded sendMessage dispatch.
  }, [
    actionParam,
    selectedAccount,
    selectedOpportunity,
    existingSessionId,
    searchParams
  ])

  const headerAskAutoTriggeredRef = useRef(false)
  useEffect(() => {
    // Header "Ask anything" input (?action=header_ask&query=...): the query
    // is free text typed by the user, not a fixed prompt, so it doesn't wait
    // on selectedAccount/selectedOpportunity like the sales-pitch deeplink.
    if (
      actionParam === "header_ask" &&
      headerAskQueryParam &&
      !headerAskAutoTriggeredRef.current &&
      !existingSessionId
    ) {
      headerAskAutoTriggeredRef.current = true
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.value = headerAskQueryParam
        }
        onSubmit({
          agentQuery: headerAskQueryParam,
          accountId: selectedAccount ?? undefined
        })
      }, 500)
    }
  }, [actionParam, headerAskQueryParam, selectedAccount, existingSessionId])

  useEffect(() => {
    // Proactive proposal entry pre-creates the os.agents session in
    // rfp-rfi before navigating to /rfp-os/proactive-proposal/:id, so
    // existingSessionId is ALWAYS truthy here.
    //
    // autoTriggeredRef alone is NOT enough to prevent a double-fire: it's
    // an in-memory ref that resets to false on a hard refresh, while
    // `?action=proactive_proposal` stays in the URL. On reload the form
    // data in `location.state` is gone, so re-firing here sends a
    // CONTENTLESS default query ("Generate a proactive sales proposal
    // deck. Slide count: 8") into the same session and clobbers the
    // original — exactly the "refresh loses context" QA bug.
    //
    // Two guards close that hole:
    //   1. Require fresh form data in location.state — a reload has none,
    //      so the default query can never fire.
    //   2. Strip `action` from the URL immediately after the first fire
    //      (replace, no history entry) so a subsequent refresh resumes the
    //      session normally instead of re-triggering this effect.
    const state = (location.state ?? {}) as Record<string, unknown>
    const hasFreshFormData = !!(state.projectName || state.context)
    if (
      actionParam !== "proactive_proposal" ||
      autoTriggeredRef.current ||
      !hasFreshFormData
    )
      return
    autoTriggeredRef.current = true

    const projectName = (state.projectName as string) || ""
    const context = (state.context as string) || ""
    const accountName = (state.accountName as string) || ""
    // Optional account the user picked on the proactive form. Sent as a
    // structured account_id (body.account_id) so the researcher fetches CRM
    // account research via generate_account_digest_or_plan. accountName is
    // still injected into the query text below for the LLM's reading.
    const accountId =
      state.accountId != null && state.accountId !== ""
        ? String(state.accountId)
        : ""
    const industryRaw = state.industry
    const industry = Array.isArray(industryRaw)
      ? (industryRaw as string[]).join(", ")
      : (industryRaw as string) || ""
    const slideCount = (state.slideCount as number) || 8

    const parts = [
      "Generate a proactive sales proposal deck.",
      projectName ? `Project: ${projectName}` : null,
      accountName ? `Account: ${accountName}` : null,
      industry ? `Industry: ${industry}` : null,
      context ? `Context: ${context}` : null,
      `Slide count: ${slideCount}`
    ].filter(Boolean)

    const agentQuery = parts.join("\n")

    // Reference docs (if any) are uploaded AND ingested at the form stage
    // (rfp-rfi blocks "Generate Outline" until scoped_files_status hits 100%),
    // so by the time we land here they're already indexed under this session.
    // Fire immediately — no ingestion gate needed.
    setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.value = agentQuery
      }
      onSubmit({
        agentQuery,
        tool_id: "GenerateProactiveProposalAgent",
        source: "proactive_proposal",
        projectName,
        accountId
      }).catch(() => {
        // ENG-3542: this used to swallow the failure entirely, leaving the
        // session with no turn, no error and a permanently stuck pane.
        notificationApi?.error({
          message: t("rfp_os.label_proposal_start_failed"),
          placement: "bottomRight"
        })
        setShowSplitter({
          resourceName: "ProactiveProposalUnavailable",
          resourceParams: {}
        } as any)
      })
    }, 500)

    // Drop `action` from the URL so a refresh resumes this session instead
    // of re-firing the auto-generate. `replace` keeps it out of history.
    // The effect re-runs when actionParam clears, but autoTriggeredRef is
    // already true so it's an immediate no-op.
    setSearchParams(
      (prev) => {
        prev.delete("action")
        return prev
      },
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionParam, existingSessionId, location.state])

  const isChatBusy =
    !!pendingMessage || isPptBusy || isPageBusy || hasPendingTurn
  const inputSearch = (
    <div
      // Ask-welcome state: drop the caller's max-width cap so the composer
      // fills the welcome column and lines up with the account-context bar
      // above it (same width). In-chat and other modes (e.g. RFP) keep the
      // caller's `className` cap unchanged.
      className={`${
        !existingSessionId && mode === "ask" ? "" : className
      } mx-auto w-full flex flex-col gap-2 ${
        isChatBusy ? "pointer-events-none opacity-95" : ""
      }`}
    >
      <AgentChatInput
        ref={chatInputRef}
        placeholder={
          mode === "rfp"
            ? t("os_agents.label_ask_your_rfp_question")
            : t("os_agents.label_ask_anything")
        }
        isLoading={isChatBusy}
        inputRef={messageRef}
        notificationApi={notificationApi}
        sessionId={existingSessionId || ""}
        ensureSessionId={ensureSessionId}
        parentSessionId={parentSessionId}
        maxFiles={20}
        maxFileSizeMB={10}
        historyFileNames={historyFileNames}
        autoOpenTemplatePicker={autoOpenTemplatePicker}
        leftControls={
          // Account picker is Ask-OS-only chrome — RFP-OS doesn't
          // surface accounts at this layer. Match prod V1 RFP UI.
          // Also suppress on the proactive-proposal route: the deck is
          // for an unbound prospect, not an existing tenant account.
          mode === "ask" && existingSessionId && !isProactiveProposalRoute ? (
            <AgentSelectAccount
              hideLabel
              accountIdOverride={accountIdOverride}
            />
          ) : null
        }
        showDeepResearch={mode === "rfp"}
        deepResearchDefaultOn={mode === "rfp"}
        deepResearchTooltip={
          mode === "rfp"
            ? t("os_agents.label_deep_research_rfp_tooltip")
            : undefined
        }
        quotedText={quotedText}
        onClearQuote={() => setQuotedText(null)}
        onSend={async (message, context) => {
          track(EVENTS.SEND_CLICKED, {
            ...getBaseEventProps({ feature: "os_agents" }),
            action: "send"
          })
          await onSubmit({
            message,
            files: context.attachedFiles.map((f) => ({
              name: f.name,
              id: f.fileId,
              source: f.source,
              is_template: f.isTemplate === true,
              original_s3_path: f.s3Path,
              file_extension: (f.name || "").split(".").pop()?.toLowerCase()
            })),
            researchEnabled: context.researchEnabled,
            isTemplateFill: context.isTemplateFill === true
          })
        }}
      />
    </div>
  )

  return (
    <>
      <div className={existingSessionId ? "" : "flex-1 flex overflow-hidden"}>
        <div className="flex-1 flex flex-col text-dark-2 dark:text-dark-6 overflow-hidden dark:bg-dark-3">
          {/*
            ENG-1249: inputSearch (which contains AgentChatInput +
            useAgentFileUpload state) must live at a STABLE tree
            position across the existingSessionId flip. Previously this
            block rendered inputSearch INSIDE the welcome wrapper when
            fresh and at TOP level when existing — different parents,
            different positions, so React unmounted AgentChatInput when
            createSession's setSubSessionId flipped existingSessionId
            mid-attach, discarding the queued setFiles(seeds) and
            leaving the chip invisible. Now the wrapper structure is
            constant; only the inner welcome chrome + classes toggle.
          */}
          <div
            className={
              !existingSessionId ? "overflow-auto flex flex-1" : "contents"
            }
          >
            <div
              className={
                !existingSessionId ? "max-w-4xl mx-auto w-full" : "contents"
              }
            >
              <div
                className={
                  !existingSessionId
                    ? mode === "rfp"
                      ? "flex-1 flex flex-col gap-4 px-6 py-3 items-center justify-center min-h-[60vh]"
                      : "flex-1 flex flex-col gap-6 px-6 py-3"
                    : "contents"
                }
              >
                {!existingSessionId && (
                  <h3
                    className={
                      mode === "rfp"
                        ? "text-lg font-medium tracking-wide text-dark-2 dark:text-dark-6"
                        : "text-lg font-medium tracking-wide text-gray-400 dark:text-dark-6 mt-4"
                    }
                  >
                    {mode === "rfp"
                      ? t("os_agents.label_ask_your_rfp_question")
                      : t("os_agents.label_welcome_explore_today")}
                  </h3>
                )}
                {/* Account context bar + tool cards are Ask-OS-only, and
                    hidden on the proactive-proposal route (it pitches an
                    unbound prospect, so no account picker) and in embedded
                    drawer mode. */}
                {!existingSessionId &&
                  mode === "ask" &&
                  !isProactiveProposalRoute && (
                    // Account context bar (askov-ctxbar): hint on the left,
                    // account selector on the right, in one bordered row.
                    <div className="flex items-center justify-between gap-4 min-h-[64px] rounded-xl border border-light-border dark:border-gray-600 bg-white dark:bg-dark-1 px-4 py-4">
                      <p className="m-0 text-[13px] leading-[1.45] text-gray-400 dark:text-dark-6 text-pretty">
                        {t("os_agents.label_account_optional_hint")}
                      </p>
                      <div className="shrink-0">
                        <AgentSelectAccount
                          hideLabel
                          accountIdOverride={accountIdOverride}
                        />
                      </div>
                    </div>
                  )}
                {inputSearch}
                {!existingSessionId &&
                  mode === "ask" &&
                  !embedded &&
                  !isProactiveProposalRoute && (
                    <div className="grid grid-cols-3 items-stretch gap-3 w-full px-4">
                      {toolsList?.map((item: Tool) => (
                        <ToolCard
                          key={item.id}
                          item={item}
                          isLoading={pendingMessage === item.id}
                          onClick={() => {
                            if (selectedAccount) {
                              const pmbCard = isPreMeetingBriefTool(item)
                              onSubmit({
                                tool_id: item.id,
                                agentQuery: item.description,
                                ...(pmbCard
                                  ? {
                                      transferToAgent:
                                        "GeneratePreMeetingBriefAgentV2",
                                      pmbContext: { mode: "default" },
                                      pmbForceDefault: true
                                    }
                                  : { transferToAgent: item.id })
                              })
                            } else {
                              if (messageRef.current) {
                                messageRef.current.value = item.description
                              }
                              chatInputRef.current?.focus()
                            }
                          }}
                        />
                      ))}
                      {otherToolsList && otherToolsList?.length > 0 && (
                        <button
                          onClick={() => {
                            track(EVENTS.MORE_TOOLS_TOGGLED, {
                              ...getBaseEventProps({ feature: "os_agents" }),
                              is_open: String(!openMore)
                            })
                            setOpenMore(!openMore)
                          }}
                          className="flex flex-col group text-left"
                        >
                          <div
                            className={`relative h-36 border-[0.5px] rounded-t-lg bg-linear-to-r from-[#FAFAFA] to-[rgba(219, 218, 224, 0.95)] dark:from-[#3C3C3E] dark:to-[#1e1e1e] shadow-inner group-hover:border-primary dark:group-hover:border-gray-400 ${openMore ? "border-primary" : "border-light-border dark:border-gray-600"}`}
                          >
                            <img
                              src="/images/os-agent/more.png"
                              alt={t("os_agents.label_more")}
                              className="object-contain h-full w-full scale-75"
                            />
                          </div>
                          <div
                            className={`flex flex-col gap-1 p-2 border-[0.5px] border-t-0 rounded-b-lg flex-1 ${openMore ? "border-primary" : "group-hover:border-primary dark:group-hover:border-gray-400 border-light-border dark:border-gray-600"}`}
                          >
                            <h6 className="text-xs font-medium">
                              {t("os_agents.label_more_tools", {
                                count: otherToolsList?.length ?? 0
                              })}
                            </h6>
                          </div>
                        </button>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
        {openMore && !existingSessionId && (
          <Suspense fallback={null}>
            <OtherTools
              otherTools={otherToolsList ?? []}
              setOpenMore={setOpenMore}
              onClick={(submitObj) => {
                // Same PMB default-mode rule as the primary tool cards:
                // a "Pre-meeting brief" entry in the more-tools list also
                // goes straight to the default brief.
                const tool = (otherToolsList ?? []).find(
                  (x: Tool) => x.id === submitObj.tool_id
                )
                if (tool && isPreMeetingBriefTool(tool)) {
                  onSubmit({
                    ...submitObj,
                    transferToAgent: "GeneratePreMeetingBriefAgentV2",
                    pmbContext: { mode: "default" },
                    pmbForceDefault: true
                  })
                  return
                }
                onSubmit({
                  ...submitObj,
                  ...(tool ? { transferToAgent: tool.id } : {})
                })
              }}
              onPopulate={(query) => {
                setOpenMore(false)
                if (messageRef.current) {
                  messageRef.current.value = query
                }
                chatInputRef.current?.focus()
              }}
            />
          </Suspense>
        )}
      </div>
      {notificationContextHolder}
    </>
  )
}

export default memo(AgentSearch)
